import { randomUuid } from "@models/utils/uuid";
import {
  DynamoDBClient,
  QueryCommand,
  GetItemCommand,
  BatchGetItemCommand,
  PutItemCommand,
  UpdateItemCommand,
  DeleteItemCommand,
  BatchWriteItemCommand,
  TransactWriteItemsCommand,
  type AttributeValue,
  type TransactWriteItem,
  type WriteRequest,
} from "@aws-sdk/client-dynamodb";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";
import {
  S3Client,
  GetObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
  NoSuchKey,
} from "@aws-sdk/client-s3";
import type { GetObjectCommandOutput } from "@aws-sdk/client-s3";
import { fetchAuthSession } from "aws-amplify/auth";

const K_DYNAMO_TRANSACTION_MAX_ITEMS = 100;
const K_DYNAMO_BATCH_GET_MAX_KEYS = 100;

import {
  sketchStoragePath,
  type IStorageModel,
  type SceneEnvironmentAttributes,
  type SketchBlobKind,
  type ThumbnailBlob,
  type TransferProgress,
} from "./IStorageModel";
import type { SkyboxData } from "@models/data/skyboxData";
import {
  assetObjectKeyForFormat,
  type AssetDto,
} from "@models/data/assetDto";
import { toNameFloatMap } from "@models/data/nameFloatMap";
import {
  ANNOTATION_REPLY_SK_SEGMENT,
  annotationReplySk,
  annotationSk,
  parseAnnotationIdFromReplySk,
  type AnnotationDto,
  type AnnotationQueryResult,
  type AnnotationReplyDto,
} from "@models/data/annotationDto";
import type {
  EntitySettingsPatch,
  EntitySnapshotPatch,
  Vec3,
} from "@models/data/entityData";
import { aliasesToDto, readAliases } from "@models/data/aliasData";
import {
  DOCS_MANIFEST_STORAGE_PATH,
  VECTOR_STORE_SETTINGS_STORAGE_PATH,
  docThumbnailStoragePath,
  workflowVectorManifestStoragePath,
} from "@models/data/docsManifestDto";
import type { EntitySettingsDto } from "@models/data/entitySettingsDto";
import type { EntitySnapshotDto } from "@models/data/entitySnapshotDto";
import {
  folderDtoToItem,
  folderIdFromMapSk,
  folderMapItem,
  folderMapSk,
  folderPk,
  folderSk,
  isFolderMapSk,
  mapFolderDtoToInfo,
  type FolderDto,
  type FolderInfo,
  type FolderProjectMapDto,
} from "@models/data/folderDto";
import type { MaterialDto } from "@models/data/materialDto";
import { MEDIA_LIBRARY_MANIFEST_STORAGE_PATH } from "@models/data/mediaLibraryDto";
import type { MediaAssetDto, MediaTileDto } from "@models/data/mediaTileData";
import type { PartDto } from "@models/data/partDto";
import { productPk, productSk, type ProductDto } from "@models/data/productDto";
import { partMaterialIds } from "@models/data/partDto";
import type { ProjectDto } from "@models/data/projectDto";
import {
  PRODUCT_DOCUMENT_LINK_ENTITY_TYPE,
  documentByProductPk,
  documentPk as canonicalDocumentPk,
  documentSk as canonicalDocumentSk,
  documentNameKeyFromSk,
  isProductDocumentArchived,
  isProductDocumentLinkArchived,
  normalizeProductNameKey,
  type ProductDocumentDto,
  type ProductDocumentKey,
  type ProductDocumentLinkDto,
} from "@models/data/productDocumentDto";
import type { SceneDto } from "@models/data/sceneDto";
import type { SharedLinkDto } from "@models/data/sharedLinkDto";
import type { WorkflowSummaryDto } from "@models/data/workflowSummaryDto";
import {
  WORKFLOW_PRODUCT_REFERENCE_ENTITY_TYPE,
  workflowByProductPk,
  workflowProductReferenceSk,
  type WorkflowProductReferenceInput,
} from "@models/data/workflowProductReferenceDto";
import type {
  CuttingPlaneState,
  HiddenObjectMode,
  SceneInfo,
  SceneViewState,
} from "@models/data/sceneInfo";
import type {
  SelectionSetData,
  SelectionSetUpdateFlag,
} from "@models/data/selectionSetData";
import { type CloudConfig, loadCloudConfig } from "./cloudConfig";
import {
  decodeProjectStorageJson,
  encodeProjectStorageJson,
  entitySnapshotsStoragePath,
  filterDeletedSnapshots,
  type JsonRecord,
  mergeEntitySnapshotFile,
  mergeEntitySnapshots,
  partsFromV2Payload,
  partsStoragePath,
  partsToV2Payload,
  snapshotToV2Json,
  snapshotsFromV2Payload,
  storesInAssetStorage,
} from "./projectStorageV2";

/**
 * Pending changes to one scene's `entity-snapshots.json` (Unity
 * `EntitySnapshotFileWriteState`). Changes queued while a write is in flight
 * fold into a single follow-up read-merge-write.
 */
interface SnapshotFileQueue {
  upserts: Map<string, JsonRecord>;
  deletes: Set<string>;
  waiters: Array<{ resolve: () => void; reject: (err: unknown) => void }>;
  writing: boolean;
}

const MIN_GUID = "00000000-0000-0000-0000-000000000000";
const MAX_GUID = "ffffffff-ffff-ffff-ffff-ffffffffffff";

/**
 * `HttpStorageModel` — production {@link IStorageModel} on top of
 * AWS DynamoDB + S3, browser-side.
 *
 * Mirrors Unity's `AwsMetadataStorageModel` + `DynamoSdkclient` path
 * (`shared-application-code/.../Models/StorageModel/AWS/DynamoClient.cs`).
 * Concretely, `getProjectsByCustomer` issues the same Dynamo query
 * Unity does:
 *
 *     KeyConditionExpression = "pk = :v_pk and sk < :v_end"
 *     :v_pk  = "{customerId}:project"
 *     :v_end = "!project:{MaxGuid}"
 *
 * against `projectstore-metadata` (table name from
 * `cloudConfig.projectTableName`). The DTOs come back as marshalled
 * Dynamo `AttributeValue` records; `@aws-sdk/util-dynamodb.unmarshall`
 * converts them to JS — and because Unity stores the JSON keys
 * PascalCase (matching `ProjectData`), the result lines up 1:1 with
 * `ProjectDto` with no field renaming.
 *
 * Auth: credentials come from Amplify's `fetchAuthSession()`, which
 * exchanges the Cognito ID token for temporary AWS keys via the
 * Identity Pool — same as Unity's `GetAWsCloudCredentialsAsync`.
 */
export class HttpStorageModel implements IStorageModel {
  readonly isOnline = true;

  private m_client: DynamoDBClient | null = null;
  private m_s3: S3Client | null = null;
  private m_clientCustomerId: string | null = null;
  private readonly m_config: CloudConfig;
  private readonly m_snapshotFileQueues = new Map<string, SnapshotFileQueue>();
  private readonly m_v2OverrideWriteChains = new Map<string, Promise<void>>();
  private readonly m_editorName: () => string | null | undefined;

  /**
   * @param editorName display name of the signed-in user, read at write time
   *   to stamp `ModifiedBy` onto the project row (see {@link stampModifiedBy}).
   */
  constructor(
    config: CloudConfig = loadCloudConfig(),
    editorName: () => string | null | undefined = () => null,
  ) {
    this.m_config = config;
    this.m_editorName = editorName;
  }

  /**
   * Adds the last-editor stamp (AI-1501) to a project-row `UpdateItem` that
   * bumps `ModifiedTimestamp`: `#MB = :mb, #MBT = :ts`, where `:ts` is the
   * very value the write gives `ModifiedTimestamp`. `lastModifiedByName`
   * trusts the name only while that pairing holds, so every project-row write
   * that bumps the date must come through here — one that skips it reads as
   * "unknown editor", not as the previous one.
   *
   * With no signed-in name nothing is added; the pairing then breaks and the
   * reader falls back to `Author`, as it does after a desktop write.
   */
  private stampModifiedBy(
    names: Record<string, string>,
    values: Record<string, AttributeValue>,
    setClause: string,
  ): {
    names: Record<string, string>;
    values: Record<string, AttributeValue>;
    setClause: string;
  } {
    const editor = this.m_editorName()?.trim();
    if (!editor) return { names, values, setClause };
    return {
      names: { ...names, "#MB": "ModifiedBy", "#MBT": "ModifiedByTimestamp" },
      values: { ...values, ":mb": { S: editor } },
      setClause: `${setClause}, #MB = :mb, #MBT = :ts`,
    };
  }

  async getProjectsByCustomer(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ProjectDto>> {
    if (!customerId) return [];
    const client = await this.getClient(customerId);

    const items: Record<string, AttributeValue>[] = [];
    let lastKey: Record<string, AttributeValue> | undefined;

    // Paginate `LastEvaluatedKey` exactly like Unity's `PagedFetch`
    // (`shared-application-code/.../AWSDynamoHelpers.cs`). Dynamo caps
    // each Query response at 1MB; large catalogs need multiple round
    // trips to surface the full list to the UI.
    do {
      const command = new QueryCommand({
        TableName: this.m_config.projectTableName,
        KeyConditionExpression: "pk = :v_pk and sk < :v_end",
        ExpressionAttributeValues: {
          ":v_pk": { S: `${customerId}:project` },
          ":v_end": { S: this.m_config.skEndSentinel },
        },
        ExclusiveStartKey: lastKey,
      });
      const out = await client.send(command, { abortSignal: signal });
      if (out.Items) items.push(...out.Items);
      lastKey = out.LastEvaluatedKey;
    } while (lastKey);

    return items.map(toProjectDto);
  }

  async getScenes(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SceneDto>> {
    if (!customerId || !projectId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetScenes` (`AWSDynamoHelpers.QueryByPkSk_Range`):
    //   pk = "{customerId}:project"
    //   sk between "#project:{projectId}:scene:{MinGuid}"
    //               and "#project:{projectId}:scene:{MaxGuid}"
    const items: Record<string, AttributeValue>[] = [];
    let lastKey: Record<string, AttributeValue> | undefined;

    do {
      const command = new QueryCommand({
        TableName: this.m_config.projectTableName,
        KeyConditionExpression: "pk = :v_pk and sk between :v_start and :v_end",
        ExpressionAttributeValues: {
          ":v_pk": { S: `${customerId}:project` },
          ":v_start": { S: `#project:${projectId}:scene:${MIN_GUID}` },
          ":v_end": { S: `#project:${projectId}:scene:${MAX_GUID}` },
        },
        ExclusiveStartKey: lastKey,
      });
      const out = await client.send(command, { abortSignal: signal });
      if (out.Items) items.push(...out.Items);
      lastKey = out.LastEvaluatedKey;
    } while (lastKey);

    // Mirrors `DynamoClient.ToSceneData`'s null guard (`DynamoClient.cs:1487`):
    // chaotic delete/reorder sequences can leave scene rows without an `ID`
    // (see the C3D-1658 note upstream). Unity drops them; so do we.
    return items
      .map((item) => unmarshall(item) as SceneDto)
      .filter((dto) => typeof dto.ID === "string" && dto.ID.length > 0);
  }

  async createScene(
    customerId: string,
    projectId: string,
    scene: SceneInfo,
    signal?: AbortSignal,
    extraAttrs?: Readonly<Record<string, unknown>>,
  ): Promise<void> {
    if (!customerId || !projectId || !scene?.id) {
      throw new Error(
        "[HttpStorageModel] createScene: customerId + projectId + scene.id required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.ToSceneItem` + `CreateScene`
    // (`DynamoClient.cs:1646-1792`, `1841`):
    //   pk = "{customerId}:project"
    //   sk = "#project:{projectId}:scene:{sceneId}"
    //   Type = "scene"
    //
    // We write the subset of `ToSceneItem` attributes the web + Unity
    // readers actually consume for an empty scene; `ToSceneData` /
    // `mapSceneDtoToInfo` both fill the remaining fields from their safe
    // defaults, so the row round-trips identically in either client.
    // Attribute names + Dynamo types (N / S / BOOL) match Unity exactly.
    const now = new Date().toISOString();
    const skybox = scene.skybox;
    const item: Record<string, AttributeValue> = {
      pk: { S: `${customerId}:project` },
      sk: { S: `#project:${projectId}:scene:${scene.id}` },
      Type: { S: "scene" },
      ID: { S: scene.id },
      Name: { S: scene.name },
      Index: { N: String(scene.index) },
      CreatedTimestamp: { S: now },
      ModifiedTimestamp: { S: scene.modifiedAt || now },
      TransitionSpeed: { N: String(scene.transitionSpeed) },
      ShowHiddenAs: { N: String(scene.hiddenObjectMode === "hidden" ? 1 : 0) },
      GridVisible: { BOOL: scene.gridVisible },
      ShadowsVisiblePerDevice: { N: String(scene.shadowsVisibleMask) },
      ConsoleVisible: { BOOL: scene.consoleVisible },
      TableVisible: { BOOL: scene.tableVisible },
      LightMode: { N: String(scene.lightMode) },
      SkyboxMode: { N: String(skybox.mode) },
      SkyboxTextureID: { S: skybox.textureId },
      SkyboxFormat: { S: skybox.format },
      SkyboxIntensity: { N: String(skybox.intensity) },
      SkyboxRotation: { N: String(skybox.rotation) },
      // Unity `ToSceneItem` writes the cutting-plane batch on create too
      // (`DynamoClient.cs:1671-1676`, `1778-1779`).
      CuttingPlaneVisible: { BOOL: scene.cuttingPlane.visible },
      CuttingPlaneExist: { BOOL: scene.cuttingPlane.exist },
      CuttingPlanePosition: vectorAttribute(scene.cuttingPlane.position),
      CuttingPlaneRotation: vectorAttribute(scene.cuttingPlane.euler),
      UsePartColor: { BOOL: scene.cuttingPlane.usePartColor },
      // Unity `ToSceneItem` writes the scene view on create too
      // (`DynamoClient.cs:1729-1731` for the bool, `:1781-1782` for the two
      // vectors). Sourced from `scene` rather than left to `extraAttrs` so
      // scene DUPLICATION carries the source's view — `duplicateScene` builds
      // its `SceneInfo` with `{ ...original }`, so the two agree.
      SceneViewPosition: vectorAttribute(scene.sceneView.position),
      SceneViewRotation: vectorAttribute(scene.sceneView.pivot),
      IsViewOrthographic: { BOOL: scene.sceneView.isOrthographic },
    };

    // Scene duplication passes the source row's remaining attributes
    // (presenter notes, asset lists, tracking marker, …) so the clone
    // keeps everything the web doesn't model. `SceneInfo`-derived attrs
    // above win; pk/sk/Type stay ours.
    if (extraAttrs) {
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(extraAttrs)) {
        if (value === undefined || value === null) continue;
        if (key === "pk" || key === "sk" || key in item) continue;
        if (Array.isArray(value) && value.length === 0) continue;
        cleaned[key] = value;
      }
      const marshalled = marshall(cleaned, {
        removeUndefinedValues: true,
        convertClassInstanceToMap: true,
      });
      for (const [key, value] of Object.entries(marshalled)) {
        item[key] = value;
      }
    }

    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: item,
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneName(
    customerId: string,
    projectId: string,
    sceneId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneName: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);
    const modifiedAt = new Date().toISOString();

    // Mirrors `DynamoClient.UpdateSceneName` (`DynamoClient.cs:1873` via
    // `CreateSceneUpdateRequest`): `SET #N = :new, #MT = :ts`. Unity writes
    // a culture-default timestamp string; ISO-8601 parses on both sides.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "Name",
          "#MT": "ModifiedTimestamp",
        },
        ExpressionAttributeValues: {
          ":new": { S: name },
          ":ts": { S: modifiedAt },
        },
        UpdateExpression: "SET #N = :new, #MT = :ts",
      }),
      { abortSignal: signal },
    );

    return { modifiedAt };
  }

  async deleteScene(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] deleteScene: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.DeleteScene` (`DynamoClient.cs:2571`): scene
    // row only — the entity/thumbnail cascade lives in the caller.
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneLock(
    customerId: string,
    projectId: string,
    sceneId: string,
    isLocked: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneLock: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneLock` (`DynamoClient.cs:2541`):
    // `SET #D = :d`, no `ModifiedTimestamp` bump.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: { "#D": "IsLocked" },
        ExpressionAttributeValues: { ":d": { BOOL: isLocked } },
        UpdateExpression: "SET #D = :d",
      }),
      { abortSignal: signal },
    );
  }

  async createCustomerProject(
    customerId: string,
    project: ProjectDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !project?.UID) {
      throw new Error(
        "[HttpStorageModel] createCustomerProject: customerId + project.UID required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.ToProjectItem` + `CreateCustomerProject`
    // (`DynamoClient.cs:154-220`, `260-266`):
    //   pk = "{customerId}:project"
    //   sk = "!project:{project.UID}"
    //   Type = "project"
    //
    // Drop empty/undefined fields per Unity's rule (`SafeSetListOfStrings`
    // skips empty lists; `ProjectPrompt` only written when non-whitespace).
    // `marshall(removeUndefinedValues: true)` handles the latter; for
    // lists we filter to non-empty arrays before marshalling.
    //
    // `AgentRole` / `AgentRoleId` are deliberately NOT written: the web port
    // treats the Spark role as session state, and Unity reads the attribute
    // with a default (`SafeGetIntAttribute(item, "AgentRole", 0)`), so an
    // absent one is the desktop client's own default role.
    const trimmedPrompt = project.ProjectPrompt?.trim();
    const cleaned: Record<string, unknown> = {
      Name: project.Name,
      Author: project.Author,
      CreatorId: project.CreatorId,
      AccountRole: project.AccountRole,
      Type: "project",
      UID: project.UID,
      CreatedTimestamp: project.CreatedTimestamp,
      ModifiedTimestamp: project.ModifiedTimestamp,
      IsDuplicating: project.IsDuplicating ?? false,
      SupportsInstances: project.SupportsInstances ?? false,
      PublishingStatus: project.PublishingStatus,
      SupportedDeviceMask: project.SupportedDeviceMask,
      SerializationVersion: project.SerializationVersion,
    };
    if (trimmedPrompt) cleaned.ProjectPrompt = trimmedPrompt;
    if (project.MaterialPresets && project.MaterialPresets.length > 0) {
      cleaned.MaterialPresets = [...project.MaterialPresets];
    }
    if (project.AssetIDs && project.AssetIDs.length > 0) {
      cleaned.AssetIDs = [...project.AssetIDs];
    }
    if (project.DocumentStoragePaths && project.DocumentStoragePaths.length > 0) {
      cleaned.DocumentStoragePaths = [...project.DocumentStoragePaths];
    }

    const marshalled = marshall(cleaned, {
      removeUndefinedValues: true,
      convertClassInstanceToMap: true,
    });

    const item: Record<string, AttributeValue> = {
      ...marshalled,
      pk: { S: `${customerId}:project` },
      sk: { S: `!project:${project.UID}` },
    };

    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: item,
      }),
      { abortSignal: signal },
    );
  }

  async updateProjectName(
    customerId: string,
    projectId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] updateProjectName: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);

    // ISO-8601 — both Unity (`DateTime.Parse`) and the web client
    // (`Date.parse`) round-trip this format. Unity writes
    // `DateTime.UtcNow.ToString()` (US-formatted by default) but
    // every reader parses it leniently, so ISO is forward-compatible.
    const modifiedAt = new Date().toISOString();

    // Mirrors Unity's `DynamoClient.UpdateCustomerProjectName`
    // (`DynamoClient.cs:333`):
    //   Key: pk = "{customerId}:project", sk = "!project:{projectId}"
    //   UpdateExpression = "SET #N = :new, #MT = :ts"
    // plus the web-only last-editor stamp.
    const stamp = this.stampModifiedBy(
      { "#N": "Name", "#MT": "ModifiedTimestamp" },
      { ":new": { S: name }, ":ts": { S: modifiedAt } },
      "SET #N = :new, #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        UpdateExpression: stamp.setClause,
      }),
      { abortSignal: signal },
    );

    return { modifiedAt };
  }

  async updateCustomerProjectAssetIDs(
    customerId: string,
    projectId: string,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] updateCustomerProjectAssetIDs: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);
    const modifiedAt = new Date().toISOString();
    // Mirrors `DynamoClient.UpdateCustomerProjectAssetIDs` (:286):
    // `SET #N = :new, #MT = :ts` where an EMPTY list writes Dynamo NULL
    // (not an empty list — Unity is explicit about that).
    const stamp = this.stampModifiedBy(
      { "#N": "AssetIDs", "#MT": "ModifiedTimestamp" },
      {
        ":new":
          assetIds.length > 0
            ? { L: assetIds.map((id) => ({ S: id })) }
            : { NULL: true },
        ":ts": { S: modifiedAt },
      },
      "SET #N = :new, #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        UpdateExpression: stamp.setClause,
      }),
      { abortSignal: signal },
    );
    return { modifiedAt };
  }

  async updateProjectConsoleAsset(
    customerId: string,
    projectId: string,
    consoleAssetId: string | null,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] updateProjectConsoleAsset: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);
    const modifiedAt = new Date().toISOString();
    const assetIdsValue =
      assetIds.length > 0
        ? { L: assetIds.map((id) => ({ S: id })) }
        : { NULL: true };

    const stamp = this.stampModifiedBy(
      {
        "#CA": "ConsoleAssetID",
        "#A": "AssetIDs",
        "#MT": "ModifiedTimestamp",
      },
      consoleAssetId
        ? {
            ":consoleAssetId": { S: consoleAssetId },
            ":assetIds": assetIdsValue,
            ":ts": { S: modifiedAt },
          }
        : {
            ":assetIds": assetIdsValue,
            ":ts": { S: modifiedAt },
          },
      consoleAssetId
        ? "SET #CA = :consoleAssetId, #A = :assetIds, #MT = :ts"
        : "SET #A = :assetIds, #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        // The stamp extends the SET clause, so the REMOVE goes after it.
        UpdateExpression: consoleAssetId
          ? stamp.setClause
          : `${stamp.setClause} REMOVE #CA`,
      }),
      { abortSignal: signal },
    );
    return { modifiedAt };
  }

  async updateProjectUserDisplaySettings(
    customerId: string,
    projectId: string,
    hideAvatars: boolean,
    hideUserTags: boolean,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] updateProjectUserDisplaySettings: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);
    const modifiedAt = new Date().toISOString();

    // Mirrors Unity's `DynamoClient.UpdateCustomerProjectUserDisplaySettings`
    // (`DynamoClient.cs:954-981`): `SET #HA = :ha, #HUT = :hut, #MT = :ts`.
    const stamp = this.stampModifiedBy(
      {
        "#HA": "HideAvatars",
        "#HUT": "HideUserTags",
        "#MT": "ModifiedTimestamp",
      },
      {
        ":ha": { BOOL: hideAvatars },
        ":hut": { BOOL: hideUserTags },
        ":ts": { S: modifiedAt },
      },
      "SET #HA = :ha, #HUT = :hut, #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        UpdateExpression: stamp.setClause,
      }),
      { abortSignal: signal },
    );

    return { modifiedAt };
  }

  async updateSceneIndex(
    customerId: string,
    projectId: string,
    sceneId: string,
    index: number,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneIndex: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "Index",
        },
        ExpressionAttributeValues: {
          ":new": { N: String(index) },
        },
        UpdateExpression: "SET #N = :new",
      }),
      { abortSignal: signal },
    );
  }

  async updateScenePresenterNotes(
    customerId: string,
    projectId: string,
    sceneId: string,
    notes: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateScenePresenterNotes: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "PresenterNotes",
        },
        ExpressionAttributeValues: {
          ":new": { S: notes },
        },
        UpdateExpression: "SET #N = :new",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneTransitionSpeed(
    customerId: string,
    projectId: string,
    sceneId: string,
    speed: number,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneTransitionSpeed: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "TransitionSpeed",
        },
        ExpressionAttributeValues: {
          ":new": { N: String(speed) },
        },
        UpdateExpression: "SET #N = :new",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneIsolationMode(
    customerId: string,
    projectId: string,
    sceneId: string,
    isolatedEntityIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneIsolationMode: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "IsolatedEntityIDs",
        },
        ExpressionAttributeValues: {
          ":new": stringListOrNull(isolatedEntityIds),
        },
        UpdateExpression: "SET #N = :new",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneHiddenMode(
    customerId: string,
    projectId: string,
    sceneId: string,
    mode: HiddenObjectMode,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneHiddenMode: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneHiddenMode` (`DynamoClient.cs:2295` via
    // `CreateSceneUpdateRequest`): `SET #N = :new, #MT = :ts`. The ordinal is
    // Unity's `HiddenObjectMode` — 0 XRay, 1 Invisible — and is the same value
    // `createScene` writes for a whole-row PUT, so the two cannot disagree.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "ShowHiddenAs",
          "#MT": "ModifiedTimestamp",
        },
        ExpressionAttributeValues: {
          ":new": { N: String(mode === "hidden" ? 1 : 0) },
          ":ts": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #N = :new, #MT = :ts",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneEnvironment(
    customerId: string,
    projectId: string,
    sceneId: string,
    env: SceneEnvironmentAttributes,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneEnvironment: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneEnvironment` (`DynamoClient.cs:2368`).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#MT": "ModifiedTimestamp",
          "#GV": "GridVisible",
          "#SV": "ShadowsVisiblePerDevice",
          "#CV": "ConsoleVisible",
          "#TV": "TableVisible",
          "#LM": "LightMode",
        },
        ExpressionAttributeValues: {
          ":ts": { S: new Date().toISOString() },
          ":gv": { BOOL: env.gridVisible },
          ":sv": { N: String(env.shadowsVisibleMask) },
          ":cv": { BOOL: env.consoleVisible },
          ":tv": { BOOL: env.tableVisible },
          ":lm": { N: String(env.lightMode) },
        },
        UpdateExpression: "SET #MT = :ts, #GV = :gv, #SV = :sv, #CV = :cv, #TV = :tv, #LM = :lm",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneCuttingPlane(
    customerId: string,
    projectId: string,
    sceneId: string,
    cuttingPlane: CuttingPlaneState,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneCuttingPlane: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneCuttingPlane` (`DynamoClient.cs:2091`).
    // Unity deliberately leaves `ModifiedTimestamp` alone in this batch.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#P": "CuttingPlanePosition",
          "#R": "CuttingPlaneRotation",
          "#V": "CuttingPlaneVisible",
          "#E": "CuttingPlaneExist",
          "#U": "UsePartColor",
        },
        ExpressionAttributeValues: {
          ":p": vectorAttribute(cuttingPlane.position),
          ":r": vectorAttribute(cuttingPlane.euler),
          ":v": { BOOL: cuttingPlane.visible },
          ":e": { BOOL: cuttingPlane.exist },
          ":u": { BOOL: cuttingPlane.usePartColor },
        },
        UpdateExpression: "SET #P = :p, #R = :r, #V = :v, #E = :e, #U = :u",
      }),
      { abortSignal: signal },
    );
  }

  /**
   * Port of `DynamoClient.UpdateSceneView` (`DynamoClient.cs:2130`).
   *
   * `view.pivot` is written to the attribute named `SceneViewRotation`
   * because that is what the desktop client reads — the attribute name is a
   * misnomer for an orbit PIVOT POSITION, not a rotation. Do not "fix" it.
   *
   * Like `UpdateSceneCuttingPlane` / `UpdateSceneSnapback`, Unity writes only
   * the three attributes and leaves `ModifiedTimestamp` alone.
   */
  async updateSceneView(
    customerId: string,
    projectId: string,
    sceneId: string,
    view: SceneViewState,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneView: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#SP": "SceneViewPosition",
          "#SR": "SceneViewRotation",
          "#VO": "IsViewOrthographic",
        },
        ExpressionAttributeValues: {
          ":sp": vectorAttribute(view.position),
          ":sr": vectorAttribute(view.pivot),
          ":vo": { BOOL: view.isOrthographic },
        },
        UpdateExpression: "SET #SP = :sp, #SR = :sr, #VO = :vo",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneShouldSnapback(
    customerId: string,
    projectId: string,
    sceneId: string,
    shouldSnapback: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneShouldSnapback: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);
    // Mirrors `DynamoClient.UpdateSceneSnapback` (`DynamoClient.cs:2398`):
    // `SET #SB = :sb` only — Unity does NOT bump ModifiedTimestamp here.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: { "#SB": "ShouldSnapback" },
        ExpressionAttributeValues: { ":sb": { BOOL: shouldSnapback } },
        UpdateExpression: "SET #SB = :sb",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneSkybox(
    customerId: string,
    projectId: string,
    sceneId: string,
    skybox: SkyboxData,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneSkybox: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneSkybox` (`DynamoClient.cs:2408`).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#MT": "ModifiedTimestamp",
          "#M": "SkyboxMode",
          "#SC": "SolidColor",
          "#CC": "CustomColor",
          "#I": "SkyboxIntensity",
          "#R": "SkyboxRotation",
          "#S": "SkyboxScale",
        },
        ExpressionAttributeValues: {
          ":ts": { S: new Date().toISOString() },
          ":m": { N: String(skybox.mode) },
          ":sc": colorAttribute(skybox.solidColor),
          ":cc": colorAttribute(skybox.customColor),
          ":i": { N: String(skybox.intensity) },
          ":r": { N: String(skybox.rotation) },
          ":s": {
            M: {
              x: { N: String(skybox.scale.x) },
              y: { N: String(skybox.scale.y) },
            },
          },
        },
        UpdateExpression: "SET #MT = :ts, #M = :m, #SC = :sc, #CC = :cc, #I = :i, #R = :r, #S = :s",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneSkyboxTexture(
    customerId: string,
    projectId: string,
    sceneId: string,
    skybox: SkyboxData,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneSkyboxTexture: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneSkyboxTexture` (`DynamoClient.cs:2449`).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#ST": "SkyboxTimestamp",
          "#T": "SkyboxTextureID",
          "#F": "SkyboxFormat",
        },
        ExpressionAttributeValues: {
          ":ts": { S: skybox.modifiedAt },
          ":tid": { S: skybox.textureId },
          ":f": { S: skybox.format },
        },
        UpdateExpression: "SET #ST = :ts, #T = :tid, #F = :f",
      }),
      { abortSignal: signal },
    );
  }

  async getSkyboxBlobUrl(
    customerId: string,
    projectId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !projectId || !textureId) return null;
    const s3 = await this.getS3Client();

    // `PathTemplates.k_skyboxPathTemplate` — "{projectId}/skyboxes/{textureId}"
    // (no extension on the object key) under the customer projectdata prefix.
    const key = `account/${customerId}/projectdata/${projectId}/skyboxes/${textureId}`;
    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
        }),
        { abortSignal: signal },
      );
      const body = out.Body;
      if (!body) return null;
      const blob = await new Response(body as BodyInit).blob();
      return URL.createObjectURL(
        out.ContentType ? new Blob([blob], { type: out.ContentType }) : blob,
      );
    } catch (err) {
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      if (status === 404 || status === 403) return null;
      console.warn(`[HttpStorageModel] S3 GetObject failed for ${key}:`, err);
      return null;
    }
  }

  async putSkyboxObject(
    customerId: string,
    projectId: string,
    textureId: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !textureId) {
      throw new Error(
        "[HttpStorageModel] putSkyboxObject: customerId + projectId + textureId required",
      );
    }
    const s3 = await this.getS3Client();
    const key = `account/${customerId}/projectdata/${projectId}/skyboxes/${textureId}`;
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: key,
        Body: blob,
        ContentType: blob.type || undefined,
      }),
      { abortSignal: signal },
    );
  }

  async deleteSkyboxObject(
    customerId: string,
    projectId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !textureId) return;
    const s3 = await this.getS3Client();
    const key = `account/${customerId}/projectdata/${projectId}/skyboxes/${textureId}`;
    await s3.send(
      new DeleteObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: key,
      }),
      { abortSignal: signal },
    );
  }

  async deleteThumbnailObject(
    customerId: string,
    projectId: string,
    thumbnailId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !thumbnailId) return;
    const s3 = await this.getS3Client();
    // Same key template as `getProjectThumbnailBlobUrl` — no `.png` suffix
    // (`PathTemplates` thumbnail storage path; only Unity's local cache
    // appends an extension). Unity deletes with `LogAndContinueIfNotFound`.
    const key = `account/${customerId}/projectdata/${projectId}/thumbnails/${thumbnailId}`;
    try {
      await s3.send(
        new DeleteObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
        }),
        { abortSignal: signal },
      );
    } catch (err) {
      console.warn(`[HttpStorageModel] thumbnail delete failed for ${key} (continuing):`, err);
    }
  }

  async deleteCustomerProject(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] deleteCustomerProject: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors Unity's `DynamoClient.DeleteCustomerProject`
    // (`DynamoClient.cs:416`) — single project-row delete; Unity
    // also fans out a `GetSharedLinksByProject` + `DeleteSharedLink`
    // loop here, but the web port doesn't model shared links yet
    // (deferred to the M10 sharing slice).
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
      }),
      { abortSignal: signal },
    );
  }

  async getEntitySnapshots(
    customerId: string,
    projectId: string,
    sceneId: string,
    learnerCode?: string,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<ReadonlyArray<EntitySnapshotDto>> {
    if (!customerId || !projectId || !sceneId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetEntitySnapshots`
    // (`DynamoClient.cs:2652`) — single non-parallel range query.
    // Sort-key prefix (`EntitySnapshotSortKey`,
    // `DynamoClient.cs:117-121`):
    //   base = "$project:{projectId}:scene:{sceneId}:snapshot:{snapshotId}"
    //   instance overlay (when `learnerCode` set) = "U#{learnerCode}{base}"
    const base = `$project:${projectId}:scene:${sceneId}:snapshot:`;
    const prefix = learnerCode ? `U#${learnerCode}${base}` : base;
    const overridesPromise = this.queryAll<EntitySnapshotDto>(
      client,
      `${customerId}:project`,
      `${prefix}${MIN_GUID}`,
      `${prefix}${MAX_GUID}`,
      signal,
    );

    // Unity keeps learner-instance rows entirely in Dynamo. V1 projects also
    // use Dynamo as their base store, so neither should probe the V2 S3 path.
    if (learnerCode || serializationVersion === 1) {
      return filterDeletedSnapshots(await overridesPromise);
    }

    // V2 moved the immutable base snapshot set to S3 and reserves Dynamo for
    // overrides. Read both concurrently, then merge in the same order as
    // Unity's `StorageModel.GetSceneEntitySnapshots`.
    const [overrides, payload] = await Promise.all([
      overridesPromise,
      this.getProjectStorageJson(
        customerId,
        entitySnapshotsStoragePath(projectId, sceneId),
        signal,
      ),
    ]);
    if (payload === null) {
      // Unknown/legacy projects and partially migrated data still render from
      // Dynamo. Known V2 projects also get this tolerant fallback rather than
      // opening an empty scene when their base object is missing.
      return filterDeletedSnapshots(overrides);
    }

    return mergeEntitySnapshots(
      snapshotsFromV2Payload(payload),
      overrides,
    );
  }

  async getEntitySettings(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<EntitySettingsDto>> {
    if (!customerId || !projectId || !sceneId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetEntitySettings`
    // (`DynamoClient.cs:2873`).
    const prefix = `$project:${projectId}:scene:${sceneId}:entity:`;
    return this.queryAll<EntitySettingsDto>(
      client,
      `${customerId}:project`,
      `${prefix}${MIN_GUID}`,
      `${prefix}${MAX_GUID}`,
      signal,
    );
  }

  async getSelectionSets(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SelectionSetData>> {
    if (!customerId || !projectId || !sceneId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetSelectionSets` (`DynamoClient.cs:4119`).
    const prefix = `$project:${projectId}:scene:${sceneId}:selectionset:`;
    return this.queryAll<SelectionSetData>(
      client,
      `${customerId}:project`,
      `${prefix}${MIN_GUID}`,
      `${prefix}${MAX_GUID}`,
      signal,
    );
  }

  async createSelectionSet(
    customerId: string,
    projectId: string,
    sceneId: string,
    selectionSet: SelectionSetData,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || !selectionSet.ID) return;
    const client = await this.getClient(customerId);
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: {
          ...marshall(selectionSetForWrite(selectionSet), {
            removeUndefinedValues: true,
            convertClassInstanceToMap: true,
          }),
          pk: { S: `${customerId}:project` },
          sk: { S: selectionSetSk(projectId, sceneId, selectionSet.ID) },
        },
      }),
      { abortSignal: signal },
    );
  }

  async deleteSelectionSets(
    customerId: string,
    projectId: string,
    sceneId: string,
    selectionSetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || selectionSetIds.length === 0) return;
    const client = await this.getClient(customerId);
    await Promise.all(
      selectionSetIds.map((id) =>
        client.send(
          new DeleteItemCommand({
            TableName: this.m_config.projectTableName,
            Key: {
              pk: { S: `${customerId}:project` },
              sk: { S: selectionSetSk(projectId, sceneId, id) },
            },
          }),
          { abortSignal: signal },
        ),
      ),
    );
  }

  async updateSelectionSet(
    customerId: string,
    projectId: string,
    sceneId: string,
    selectionSet: SelectionSetData,
    flags: ReadonlyArray<SelectionSetUpdateFlag>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || !selectionSet.ID || flags.length === 0) return;
    const client = await this.getClient(customerId);
    const names: Record<string, string> = {};
    const values: Record<string, AttributeValue> = {};
    const sets: string[] = [];

    if (flags.includes("Name")) {
      names["#N"] = "Name";
      values[":n"] = { S: selectionSet.Name };
      sets.push("#N = :n");
    }
    if (flags.includes("Visibility")) {
      names["#V"] = "IsVisible";
      names["#F"] = "IsForcedVisible";
      values[":v"] = { BOOL: selectionSet.IsVisible !== false };
      values[":f"] = { BOOL: selectionSet.IsForcedVisible === true };
      sets.push("#V = :v", "#F = :f");
    }
    if (flags.includes("Entities")) {
      names["#E"] = "EntitySnapshotIDs";
      names["#M"] = "MediaTileIDs";
      values[":e"] = stringListOrNull(selectionSet.EntitySnapshotIDs ?? []);
      values[":m"] = stringListOrNull(selectionSet.MediaTileIDs ?? []);
      sets.push("#E = :e", "#M = :m");
    }
    if (flags.includes("Pose")) {
      const marshalled = marshall(selectionSetForWrite(selectionSet), {
        removeUndefinedValues: true,
        convertClassInstanceToMap: true,
      });
      for (const [alias, key] of [
        ["#P", "Position"],
        ["#R", "Rotation"],
        ["#S", "Scale"],
        ["#PT", "Pivot"],
        ["#PR", "PivotR"],
        ["#PS", "PivotS"],
      ] as const) {
        const value = marshalled[key];
        if (!value) continue;
        names[alias] = key;
        values[`:${key}`] = value;
        sets.push(`${alias} = :${key}`);
      }
    }
    if (sets.length === 0) return;

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: selectionSetSk(projectId, sceneId, selectionSet.ID) },
        },
        ExpressionAttributeNames: names,
        ExpressionAttributeValues: values,
        UpdateExpression: `SET ${sets.join(", ")}`,
      }),
      { abortSignal: signal },
    );
  }

  async getParts(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<ReadonlyArray<PartDto>> {
    if (!customerId || !assetId) return [];
    const getMetadataParts = async (): Promise<ReadonlyArray<PartDto>> => {
      const client = await this.getClient(customerId);
      // Mirrors `DynamoClient.GetParts` (`DynamoClient.cs:1116`).
      // Note the partition key changes to `:asset` (parts live in the
      // asset partition, not the project partition).
      const prefix = `#asset:${assetId}:part:`;
      return this.queryAll<PartDto>(
        client,
        `${customerId}:asset`,
        `${prefix}${MIN_GUID}`,
        `${prefix}${MAX_GUID}`,
        signal,
      );
    };

    // V1 keeps parts in Dynamo. Unity also probes the V2 object when a legacy
    // asset has no rows, which heals partially migrated projects.
    let metadataParts: ReadonlyArray<PartDto> | null = null;
    if (serializationVersion === 1) {
      metadataParts = await getMetadataParts();
      if (metadataParts.length > 0) return metadataParts;
    }

    // V2 stores one gzip JSON catalog per asset.
    const payload = await this.getProjectStorageJson(
      customerId,
      partsStoragePath(assetId),
      signal,
    );
    if (payload !== null) {
      const storedParts = partsFromV2Payload(payload);
      if (storedParts.length > 0) return storedParts;
    }

    // Unknown versions (e.g. cross-account share list entries without the
    // project row) auto-detect, and known V2 projects tolerate old web-created
    // Dynamo rows when the S3 catalog is absent.
    return metadataParts ?? getMetadataParts();
  }

  async getAssetById(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<AssetDto | null> {
    if (!customerId || !assetId) return null;
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetAssetById` (`DynamoClient.cs:980`):
    // single-item lookup by exact pk+sk. Use `GetItemCommand` (1 RCU)
    // rather than `Query` since we only ever want one row.
    const out = await client.send(
      new GetItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:asset` },
          sk: { S: `!asset:${assetId}` },
        },
      }),
      { abortSignal: signal },
    );
    if (!out.Item) return null;
    return unmarshall(out.Item) as AssetDto;
  }

  // ---- Model import writes (Add Model) -----------------------------

  // ---- Project folders ---------------------------------------------
  //
  // pk = `"{customerId}:folder"`; sk = `"!folder:{id}"` (the folder) or
  // `"!folder:{id}:map:{projectId}"` (one membership). `models/data/folderDto.ts`
  // holds the contract and explains why one range query returns both kinds.

  async getFoldersByCustomer(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<FolderInfo>> {
    if (!customerId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetFoldersByCustomer` (`DynamoClient.cs:501-556`)
    // exactly: ONE range over the whole `!folder:` space, then split by sort
    // key. `queryAll` unmarshalls the full item including `sk`, which is the
    // discriminator, so no raw-item pass is needed.
    const rows = await this.queryAll<FolderDto & FolderProjectMapDto>(
      client,
      folderPk(customerId),
      folderSk(MIN_GUID),
      folderSk(MAX_GUID),
      signal,
    );

    const folders = new Map<string, FolderInfo>();
    const memberships: Array<{ folderId: string; projectId: string }> = [];

    for (const row of rows) {
      if (isFolderMapSk(row.sk)) {
        const folderId = folderIdFromMapSk(row.sk as string);
        // Unity reads `ProjectID` off the row rather than parsing it out of the
        // sk (`DynamoClient.cs:538`) — the attribute is authoritative.
        if (folderId && row.ProjectID) {
          memberships.push({ folderId, projectId: row.ProjectID });
        }
        continue;
      }
      if (typeof row.ID === "string" && row.ID.length > 0) {
        folders.set(row.ID, mapFolderDtoToInfo(row, customerId));
      }
    }

    // A map row whose folder row is missing is DROPPED, not turned into a
    // folder — Unity's `TryGetValue` does the same. That state is reachable:
    // `DeleteFolder` removes the folder row first and its map rows second, so a
    // read landing between the two must not resurrect the folder.
    const projectIdsByFolder = new Map<string, string[]>();
    for (const { folderId, projectId } of memberships) {
      if (!folders.has(folderId)) continue;
      const list = projectIdsByFolder.get(folderId);
      if (list) list.push(projectId);
      else projectIdsByFolder.set(folderId, [projectId]);
    }

    return [...folders.values()].map((folder) => ({
      ...folder,
      projectIds: projectIdsByFolder.get(folder.id) ?? [],
    }));
  }

  async createFolder(
    customerId: string,
    folder: FolderDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !folder?.ID) {
      throw new Error("[HttpStorageModel] createFolder: customerId + folder.ID required");
    }
    const client = await this.getClient(customerId);
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: folderDtoToItem(customerId, folder),
      }),
      { abortSignal: signal },
    );
  }

  async updateFolderName(
    customerId: string,
    folderId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !folderId) {
      throw new Error("[HttpStorageModel] updateFolderName: customerId + folderId required");
    }
    const client = await this.getClient(customerId);
    // `DynamoClient.UpdateFolderName` (`:568`) is `SET #N = :new` and nothing
    // else — deliberately no `ModifiedTS` touch, unlike the project-row rename.
    // Do not "fix" that: the folder's effective modified time is derived from
    // its projects (`folderDto.folderModifiedAt`), so stamping the row here
    // would make a rename outrank a real edit inside the folder.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: folderPk(customerId) },
          sk: { S: folderSk(folderId) },
        },
        ExpressionAttributeNames: { "#N": "Name" },
        ExpressionAttributeValues: { ":new": { S: name } },
        UpdateExpression: "SET #N = :new",
      }),
      { abortSignal: signal },
    );
  }

  async addProjectsToFolder(
    customerId: string,
    folderId: string,
    projectIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !folderId) {
      throw new Error("[HttpStorageModel] addProjectsToFolder: customerId + folderId required");
    }
    if (projectIds.length === 0) return;
    const client = await this.getClient(customerId);
    await this.batchWrite(
      client,
      projectIds.map((projectId) => ({
        PutRequest: { Item: folderMapItem(customerId, folderId, projectId) },
      })),
      signal,
    );
  }

  async removeProjectsFromFolder(
    customerId: string,
    folderId: string,
    projectIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !folderId) {
      throw new Error(
        "[HttpStorageModel] removeProjectsFromFolder: customerId + folderId required",
      );
    }
    if (projectIds.length === 0) return;
    const client = await this.getClient(customerId);
    await this.batchWrite(
      client,
      projectIds.map((projectId) => ({
        DeleteRequest: {
          Key: {
            pk: { S: folderPk(customerId) },
            sk: { S: folderMapSk(folderId, projectId) },
          },
        },
      })),
      signal,
    );
  }

  async deleteFolder(
    customerId: string,
    folderId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !folderId) {
      throw new Error("[HttpStorageModel] deleteFolder: customerId + folderId required");
    }
    const client = await this.getClient(customerId);

    // Unity's order (`DynamoClient.cs:654-680`): the folder row goes FIRST,
    // then its map rows are queried and batch-deleted. Kept as-is — it is the
    // order that fails safe, because a crash between the two leaves orphan map
    // rows that every reader already ignores (see `getFoldersByCustomer`),
    // whereas the reverse would leave a folder that has silently lost its
    // contents and still renders.
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: folderPk(customerId) },
          sk: { S: folderSk(folderId) },
        },
      }),
      { abortSignal: signal },
    );

    const mapRows = await this.queryAll<FolderProjectMapDto>(
      client,
      folderPk(customerId),
      folderMapSk(folderId, MIN_GUID),
      folderMapSk(folderId, MAX_GUID),
      signal,
    );
    if (mapRows.length === 0) return;

    await this.batchWrite(
      client,
      mapRows
        .filter((row) => typeof row.sk === "string" && row.sk.length > 0)
        .map((row) => ({
          DeleteRequest: {
            Key: {
              pk: { S: folderPk(customerId) },
              sk: { S: row.sk as string },
            },
          },
        })),
      signal,
    );
  }

  // ---- Shared links (project sharing) ------------------------------
  //
  // Row key (DynamoClient.cs key guide): pk = `"{customerId}:sharedlinkId"`
  // (literal suffix, lowercase `l` + capital `I`), sk = the bare link id.

  async getAllSharedLinks(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedLinkDto>> {
    if (!customerId) return [];
    const client = await this.getClient(customerId);
    // Unity `GetAllSharedLinks` bounds with `sk < MaxGuid`; the ids are
    // 32-hex Hash128 strings, so a `between` from "" is equivalent — use
    // an explicit low bound the range query accepts.
    return this.queryAll<SharedLinkDto>(
      client,
      `${customerId}:sharedlinkId`,
      "0",
      MAX_GUID,
      signal,
    );
  }

  async getSharedLinksByProject(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedLinkDto>> {
    if (!customerId || !projectId) return [];
    const all = await this.getAllSharedLinks(customerId, signal);
    // Unity applies a Dynamo FilterExpression on `ProjectID` and then drops
    // rows with an empty `SharedWithEmail`. Filtering client-side costs the
    // same RCUs (a filter runs after the read) and keeps one query path.
    return all.filter(
      (link) => link.ProjectID === projectId && Boolean(link.SharedWithEmail),
    );
  }

  async getSharedLink(
    customerId: string,
    sharedLinkId: string,
    signal?: AbortSignal,
  ): Promise<SharedLinkDto | null> {
    if (!customerId || !sharedLinkId) return null;
    const client = await this.getClient(customerId);
    const out = await client.send(
      new GetItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:sharedlinkId` },
          sk: { S: sharedLinkId },
        },
      }),
      { abortSignal: signal },
    );
    if (!out.Item) return null;
    return unmarshall(out.Item) as SharedLinkDto;
  }

  async createSharedLink(
    customerId: string,
    link: SharedLinkDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !link.ID) {
      throw new Error("[HttpStorageModel] createSharedLink: customerId + link.ID required");
    }
    const client = await this.getClient(customerId);
    // Full PutItem (overwrite semantics — re-inviting the same email
    // rewrites the same deterministic row). Mirrors `ToSharedLinkItem`:
    // every string attr is `?? ""`, and `ProjectName` is deliberately NOT
    // written on create (only `UpdateSharedLinks` sets it, on rename).
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: {
          pk: { S: `${customerId}:sharedlinkId` },
          sk: { S: link.ID },
          ID: { S: link.ID },
          LinkType: { N: String(link.LinkType ?? 0) },
          CustomerID: { S: link.CustomerID ?? "" },
          ProjectID: { S: link.ProjectID ?? "" },
          SceneID: { S: link.SceneID ?? "" },
          AnnotationID: { S: link.AnnotationID ?? "" },
          SharedWithEmail: { S: link.SharedWithEmail ?? "" },
          ProjectAuthor: { S: link.ProjectAuthor ?? "" },
          UserRole: { N: String(link.UserRole ?? 0) },
          State: { N: String(link.State ?? 0) },
        },
      }),
      { abortSignal: signal },
    );
  }

  async deleteSharedLink(
    customerId: string,
    sharedLinkId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !sharedLinkId) return;
    const client = await this.getClient(customerId);
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:sharedlinkId` },
          sk: { S: sharedLinkId },
        },
      }),
      { abortSignal: signal },
    );
  }

  async updateSharedLinkUserRole(
    customerId: string,
    sharedLinkId: string,
    userRole: number,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.updateSharedLinkAttr(customerId, sharedLinkId, "UserRole", userRole, signal);
  }

  async updateSharedLinkState(
    customerId: string,
    sharedLinkId: string,
    state: number,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.updateSharedLinkAttr(customerId, sharedLinkId, "State", state, signal);
  }

  async updateSharedLinksProjectName(
    customerId: string,
    sharedLinkIds: ReadonlyArray<string>,
    projectName: string,
    signal?: AbortSignal,
  ): Promise<void> {
    // Unity denormalizes the project name onto every link row on rename.
    for (const id of sharedLinkIds) {
      await this.updateSharedLinkAttr(customerId, id, "ProjectName", projectName, signal);
    }
  }

  async updateCustomerProjectAccountRole(
    customerId: string,
    projectId: string,
    accountRole: number,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] updateCustomerProjectAccountRole: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);
    const modifiedAt = new Date().toISOString();
    // Mirrors `DynamoClient.UpdateCustomerProjectAccountRole` (:842):
    // `SET #R = :role, #MT = :ts`.
    const stamp = this.stampModifiedBy(
      { "#R": "AccountRole", "#MT": "ModifiedTimestamp" },
      { ":role": { N: String(accountRole) }, ":ts": { S: modifiedAt } },
      "SET #R = :role, #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        UpdateExpression: stamp.setClause,
      }),
      { abortSignal: signal },
    );
    return { modifiedAt };
  }

  /** Shared single-attribute UpdateItem on a shared-link row. */
  private async updateSharedLinkAttr(
    customerId: string,
    sharedLinkId: string,
    attr: "UserRole" | "State" | "ProjectName",
    value: number | string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !sharedLinkId) return;
    const client = await this.getClient(customerId);
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:sharedlinkId` },
          sk: { S: sharedLinkId },
        },
        ExpressionAttributeNames: { "#N": attr },
        ExpressionAttributeValues: {
          ":new": typeof value === "number" ? { N: String(value) } : { S: value },
        },
        // Unity fires these without awaiting the inner UpdateItem (a bug);
        // the web awaits properly.
        UpdateExpression: "SET #N = :new",
      }),
      { abortSignal: signal },
    );
  }

  async getAllAssets(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<AssetDto>> {
    if (!customerId) return [];
    const client = await this.getClient(customerId);
    // Mirrors `AWSDynamoHelpers.GetAllAssets`: every asset row for the
    // account. Unity bounds it with `sk < "!asset:{MaxGuid}"`; a GUID-range
    // `between` is equivalent (asset sks are always `!asset:{guid}`) and
    // keeps this consistent with every other web read via `queryAll`.
    // Part rows (`#asset:…`) sort outside the range either way.
    const prefix = "!asset:";
    return this.queryAll<AssetDto>(
      client,
      `${customerId}:asset`,
      `${prefix}${MIN_GUID}`,
      `${prefix}${MAX_GUID}`,
      signal,
    );
  }

  /**
   * Port of `CustomerModel.LoadUserProfilePicAsync` (`:740`), which resolves
   * `IDataStorageModelExtensions.GetStoragePathForUserPic(userId)` =
   * `PathTemplates.k_userPicPathTemplate` = `UserProfilePics/{userId}`, then
   * `GetFile(naturalAccountId, storagePath)`. Unity's `ObjectKeyForCustomer`
   * (`AwsDataStorageModel.cs:123`) prefixes `account/{customerId}/projectdata/`
   * — byte-identical to every key this class already builds, so the same object
   * comes back.
   *
   * `customerId` is Unity's **naturalAccountId**: a picture is downloaded from
   * the account that owns the user, which is not necessarily the account whose
   * projects you are viewing. Unity falls back to the current customer when it
   * is empty, and so should the caller.
   */
  async getUserProfilePicBlobUrl(
    customerId: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !userId) return null;
    const key = `account/${customerId}/projectdata/UserProfilePics/${userId}`;
    try {
      const s3 = await this.getS3Client();
      const out = await s3.send(
        new GetObjectCommand({ Bucket: this.m_config.projectBucket, Key: key }),
        { abortSignal: signal },
      );
      if (!out.Body) return null;
      const bytes = await new Response(out.Body as BodyInit).arrayBuffer();
      return URL.createObjectURL(
        new Blob([bytes], { type: out.ContentType ?? "image/png" }),
      );
    } catch (err) {
      // Most users have no picture. Unity's `LoadUserProfilePicAsync` wraps the
      // fetch in `LogAndContinueIfNotFound` and only assigns when non-null, so a
      // miss silently leaves the initials showing. Same here: null, no warning.
      //
      // DEV-only breadcrumb, because the two failure modes are indistinguishable
      // from the UI: S3 answers a GetObject on a MISSING key with **403
      // AccessDenied, not 404**, whenever the caller lacks `s3:ListBucket` (Unity
      // documents the same conflation at `AwsDataStorageModel.cs:174-197`). The
      // web client has never exercised the `UserProfilePics/` prefix before, so
      // "no picture" and "IAM/CORS blocks this prefix" would otherwise both just
      // render as initials. Production stays silent.
      if (import.meta.env.DEV) {
        const e = err as { name?: string; $metadata?: { httpStatusCode?: number } };
        console.debug(
          `[HttpStorageModel] no profile picture for ${userId}:`,
          e?.name,
          e?.$metadata?.httpStatusCode,
        );
      }
      return null;
    }
  }

  async getAssetPreviewBlobUrl(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !assetId) return null;
    // Unity `PathTemplates.k_assetPreviewTemplate` = `assetpreview/{id}` —
    // extensionless key, PNG bytes (`CustomerModel.GetAssetPreviewAsync`).
    const key = `account/${customerId}/projectdata/assetpreview/${assetId}`;
    try {
      const s3 = await this.getS3Client();
      const out = await s3.send(
        new GetObjectCommand({ Bucket: this.m_config.projectBucket, Key: key }),
        { abortSignal: signal },
      );
      if (!out.Body) return null;
      const bytes = await new Response(out.Body as BodyInit).arrayBuffer();
      return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
    } catch (err) {
      // Missing preview is the norm for assets imported before previews
      // existed — Unity swallows NotFound/Forbidden and keeps the
      // placeholder sprite. Same here: null, no warning spam.
      return null;
    }
  }

  async putAssetPreviewObject(
    customerId: string,
    assetId: string,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !assetId) {
      throw new Error("[HttpStorageModel] putAssetPreviewObject: customerId + assetId required");
    }
    const s3 = await this.getS3Client();
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/assetpreview/${assetId}`,
        Body: bytes,
        ContentType: "image/png",
      }),
      { abortSignal: signal },
    );
  }

  async deleteAsset(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !assetId) return;
    const client = await this.getClient(customerId);
    // Mirrors `DynamoClient.DeleteAssets` — row only. Unity's own comment:
    // "doesn't delete the file associated with the Asset".
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:asset` },
          sk: { S: `!asset:${assetId}` },
        },
      }),
      { abortSignal: signal },
    );
  }

  async deleteParts(
    customerId: string,
    assetId: string,
    partIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !assetId || partIds.length === 0) return;
    const client = await this.getClient(customerId);
    // Every caller deletes a whole asset and the library delete has no project
    // version to consult, so the V2 catalog goes with the V1 rows.
    await this.deleteObjectTolerant(
      `account/${customerId}/projectdata/${partsStoragePath(assetId)}`,
      signal,
    );
    // Mirrors `DynamoClient.DeleteParts`: BatchWrite deletes on
    // `#asset:{assetId}:part:{partId}`.
    await this.batchWrite(
      client,
      partIds.map((partId) => ({
        DeleteRequest: {
          Key: {
            pk: { S: `${customerId}:asset` },
            sk: { S: `#asset:${assetId}:part:${partId}` },
          },
        },
      })),
      signal,
    );
  }

  async deleteAssetObject(
    customerId: string,
    objectKey: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !objectKey) return;
    await this.deleteObjectTolerant(
      `account/${customerId}/projectdata/${objectKey}`,
      signal,
    );
  }

  async deleteAssetPreviewObject(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !assetId) return;
    await this.deleteObjectTolerant(
      `account/${customerId}/projectdata/assetpreview/${assetId}`,
      signal,
    );
  }

  async putAssetObject(
    customerId: string,
    objectKey: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !objectKey) {
      throw new Error("[HttpStorageModel] putAssetObject: customerId + objectKey required");
    }
    // Pass a Uint8Array (not the Blob): the browser S3 client routes Blob
    // bodies through aws-chunked streaming, which calls `.getReader()` on a
    // non-stream and throws. A byte array has a known length → simple PUT.
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const s3 = await this.getS3Client();
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/${objectKey}`,
        Body: bytes,
        ContentType: blob.type || "model/gltf-binary",
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  async createAsset(customerId: string, asset: AssetDto, signal?: AbortSignal): Promise<void> {
    if (!customerId || !asset.ID) {
      throw new Error("[HttpStorageModel] createAsset: customerId + asset.ID required");
    }
    const client = await this.getClient(customerId);
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: assetDtoToItem(customerId, asset),
      }),
      { abortSignal: signal },
    );
  }

  async createParts(
    customerId: string,
    assetId: string,
    parts: ReadonlyArray<PartDto>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void> {
    if (!customerId || !assetId || parts.length === 0) return;
    if (storesInAssetStorage(serializationVersion)) {
      // Unity `StorageModel.CreateParts`: V2 replaces the whole catalog.
      await this.putProjectStorageJson(
        customerId,
        partsStoragePath(assetId),
        partsToV2Payload(assetId, parts),
        signal,
      );
      return;
    }
    const client = await this.getClient(customerId);
    const requests: WriteRequest[] = parts.map((part) => ({
      PutRequest: { Item: partDtoToItem(customerId, assetId, part) },
    }));
    await this.batchWrite(client, requests, signal);
  }

  // Unity's `UpdateAsset` / `UpdateParts` are literally
  // `return CreateAsset(...)` / the same `ToPartItem` BatchWrite: Dynamo
  // `PutItem` replaces the item at the same pk/sk, and Campfire always writes
  // whole objects. Delegating (rather than duplicating the marshalling) keeps
  // that equivalence explicit.
  async updateAsset(customerId: string, asset: AssetDto, signal?: AbortSignal): Promise<void> {
    await this.createAsset(customerId, asset, signal);
  }

  async updateParts(
    customerId: string,
    assetId: string,
    parts: ReadonlyArray<PartDto>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void> {
    await this.createParts(customerId, assetId, parts, signal, serializationVersion);
  }

  async createMaterials(
    customerId: string,
    materials: ReadonlyArray<MaterialDto>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || materials.length === 0) return;
    const client = await this.getClient(customerId);
    const requests: WriteRequest[] = materials
      .filter((material) => Boolean(material.ID))
      .map((material) => ({
        PutRequest: { Item: materialDtoToDynamoItem(customerId, material) },
      }));
    if (requests.length === 0) return;
    await this.batchWrite(client, requests, signal);
  }

  async deleteMaterials(
    customerId: string,
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    const ids = materialIds.filter(Boolean);
    if (!customerId || ids.length === 0) return;
    const client = await this.getClient(customerId);
    await this.batchWrite(
      client,
      ids.map((id) => ({
        DeleteRequest: {
          Key: {
            pk: { S: `${customerId}:material` },
            sk: { S: `$material:${id}` },
          },
        },
      })),
      signal,
    );
  }

  async createEntitySnapshots(
    customerId: string,
    projectId: string,
    sceneId: string,
    snapshots: ReadonlyArray<EntitySnapshotDto>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || snapshots.length === 0) return;
    if (storesInAssetStorage(serializationVersion)) {
      const upserts = new Map<string, JsonRecord>();
      for (const snap of snapshots) {
        const id = snap.SnapshotID || snap.PartID;
        if (id) upserts.set(id, snapshotToV2Json(snap));
      }
      signal?.throwIfAborted();
      await this.queueEntitySnapshotFileChanges(customerId, projectId, sceneId, upserts, []);
      return;
    }
    const client = await this.getClient(customerId);
    const requests: WriteRequest[] = snapshots.map((snap) => ({
      PutRequest: { Item: snapshotDtoToItem(customerId, projectId, sceneId, snap) },
    }));
    await this.batchWrite(client, requests, signal);
  }

  async createEntitySettings(
    customerId: string,
    projectId: string,
    sceneId: string,
    settings: EntitySettingsDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || !settings.EntityID) {
      throw new Error(
        "[HttpStorageModel] createEntitySettings: customerId + projectId + sceneId + EntityID required",
      );
    }
    const client = await this.getClient(customerId);
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: settingsDtoToItem(customerId, projectId, sceneId, settings),
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneAssetIDs(
    customerId: string,
    projectId: string,
    sceneId: string,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneAssetIDs: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);
    // Mirrors `DynamoClient.UpdateSceneAssetIDs` (`DynamoClient.cs:1926`) —
    // scene rows use the `#project:…:scene:…` key, and the write bumps
    // `VersionTag` as Unity does.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#N": "AssetIDs",
          "#MT": "ModifiedTimestamp",
          "#VT": "VersionTag",
        },
        ExpressionAttributeValues: {
          ":new": stringListOrNull(assetIds),
          ":ts": { S: new Date().toISOString() },
          ":vt": { S: randomUuid() },
        },
        UpdateExpression: "SET #N = :new, #MT = :ts, #VT = :vt",
      }),
      { abortSignal: signal },
    );
  }

  /**
   * Chunked `BatchWriteItem` (25 items/request) with an
   * `UnprocessedItems` retry, mirroring `DynamoHelpers.BatchWrite`.
   */
  /**
   * S3 `DeleteObject` that tolerates a missing key (Unity's
   * `LogAndContinueIfNotFound`) — asset binaries and previews are commonly
   * absent for legacy rows.
   */
  private async deleteObjectTolerant(key: string, signal?: AbortSignal): Promise<void> {
    try {
      const s3 = await this.getS3Client();
      await s3.send(
        new DeleteObjectCommand({ Bucket: this.m_config.projectBucket, Key: key }),
        { abortSignal: signal },
      );
    } catch (err) {
      console.warn(`[HttpStorageModel] S3 DeleteObject failed for ${key} (continuing):`, err);
    }
  }

  private async batchWrite(
    client: DynamoDBClient,
    requests: ReadonlyArray<WriteRequest>,
    signal?: AbortSignal,
  ): Promise<void> {
    const table = this.m_config.projectTableName;
    for (let i = 0; i < requests.length; i += 25) {
      let pending: WriteRequest[] = requests.slice(i, i + 25);
      for (let attempt = 0; attempt < 5 && pending.length > 0; attempt++) {
        const out = await client.send(
          new BatchWriteItemCommand({ RequestItems: { [table]: pending } }),
          { abortSignal: signal },
        );
        const unprocessed = out.UnprocessedItems?.[table];
        pending = unprocessed && unprocessed.length > 0 ? unprocessed : [];
      }
      if (pending.length > 0) {
        throw new Error(
          `[HttpStorageModel] batchWrite: ${pending.length} items unprocessed after retries`,
        );
      }
    }
  }

  async updateEntitySnapshots(
    customerId: string,
    projectId: string,
    sceneId: string,
    updates: ReadonlyArray<{
      readonly snapshotId: string;
      readonly patch: EntitySnapshotPatch;
    }>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateEntitySnapshots: customerId + projectId + sceneId required",
      );
    }
    if (updates.length === 0) return;
    if (storesInAssetStorage(serializationVersion)) {
      await this.enqueueV2SnapshotOverrideWrite(customerId, projectId, sceneId, () =>
        this.writeV2SnapshotOverrides(customerId, projectId, sceneId, updates, signal));
      return;
    }
    const pk = `${customerId}:project`;
    const prepared: Array<{
      readonly sk: string;
      readonly fields: Record<string, unknown>;
    }> = [];
    for (const update of updates) {
      if (!update.snapshotId) {
        throw new Error("[HttpStorageModel] updateEntitySnapshots: snapshotId required");
      }
      const fields = snapshotPatchToDynamoFields(update.patch);
      if (Object.keys(fields).length === 0) continue;
      prepared.push({
        sk: `$project:${projectId}:scene:${sceneId}:snapshot:${update.snapshotId}`,
        fields,
      });
    }
    if (prepared.length === 0) return;

    const client = await this.getClient(customerId);
    const [singleUpdate] = prepared;
    if (prepared.length === 1 && singleUpdate) {
      await this.updateProjectRow(client, pk, singleUpdate.sk, singleUpdate.fields, signal);
      return;
    }

    const writes: TransactWriteItem[] = prepared.flatMap((update) => {
      const write = buildProjectRowTransactionUpdate(
        this.m_config.projectTableName,
        pk,
        update.sk,
        update.fields,
      );
      return write ? [write] : [];
    });
    for (let offset = 0; offset < writes.length; offset += K_DYNAMO_TRANSACTION_MAX_ITEMS) {
      await client.send(
        new TransactWriteItemsCommand({
          TransactItems: writes.slice(offset, offset + K_DYNAMO_TRANSACTION_MAX_ITEMS),
        }),
        { abortSignal: signal },
      );
    }
  }

  async updateEntitySettings(
    customerId: string,
    projectId: string,
    sceneId: string,
    entityId: string,
    patch: EntitySettingsPatch,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || !entityId) {
      throw new Error(
        "[HttpStorageModel] updateEntitySettings: customerId + projectId + sceneId + entityId required",
      );
    }
    const fields = settingsPatchToDynamoFields(patch);
    if (Object.keys(fields).length === 0) return;
    const client = await this.getClient(customerId);
    await this.updateProjectRow(
      client,
      `${customerId}:project`,
      `$project:${projectId}:scene:${sceneId}:entity:${entityId}`,
      fields,
      signal,
    );
  }

  async deleteEntity(
    customerId: string,
    projectId: string,
    sceneId: string,
    entityId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || !entityId) {
      throw new Error(
        "[HttpStorageModel] deleteEntity: customerId + projectId + sceneId + entityId required",
      );
    }
    const client = await this.getClient(customerId);
    const pk = `${customerId}:project`;
    const deleteRow = (sk: string) =>
      client.send(
        new DeleteItemCommand({
          TableName: this.m_config.projectTableName,
          Key: { pk: { S: pk }, sk: { S: sk } },
        }),
        { abortSignal: signal },
      );

    // Unity `StorageModel.DeleteEntitySnapshots`: the file first, so a failure
    // there leaves the overrides that still describe the entity in place.
    if (storesInAssetStorage(serializationVersion) && snapshotIds.length > 0) {
      signal?.throwIfAborted();
      await this.queueEntitySnapshotFileChanges(
        customerId,
        projectId,
        sceneId,
        new Map(),
        snapshotIds,
      );
    }
    await Promise.all([
      deleteRow(`$project:${projectId}:scene:${sceneId}:entity:${entityId}`),
      ...snapshotIds.map((snapshotId) =>
        deleteRow(`$project:${projectId}:scene:${sceneId}:snapshot:${snapshotId}`),
      ),
    ]);
  }

  // ---- Media tiles / media assets (text tiles) --------------------

  async getMediaTiles(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MediaTileDto>> {
    if (!customerId || !projectId || !sceneId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetMediaTiles`.
    const prefix = `$project:${projectId}:scene:${sceneId}:mediatile:`;
    return this.queryAll<MediaTileDto>(
      client,
      `${customerId}:project`,
      `${prefix}${MIN_GUID}`,
      `${prefix}${MAX_GUID}`,
      signal,
    );
  }

  async getMediaAssets(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MediaAssetDto>> {
    if (!customerId || !projectId) return [];
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.GetMediaAssets`.
    const prefix = `$project:${projectId}:mediaasset:`;
    return this.queryAll<MediaAssetDto>(
      client,
      `${customerId}:project`,
      `${prefix}${MIN_GUID}`,
      `${prefix}${MAX_GUID}`,
      signal,
    );
  }

  async getMediaAsset(
    customerId: string,
    projectId: string,
    mediaAssetId: string,
    signal?: AbortSignal,
  ): Promise<MediaAssetDto | null> {
    if (!customerId || !projectId || !mediaAssetId) return null;
    const client = await this.getClient(customerId);
    const out = await client.send(
      new GetItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: mediaAssetSk(projectId, mediaAssetId) },
        },
      }),
      { abortSignal: signal },
    );
    if (!out.Item) return null;
    return unmarshall(out.Item) as MediaAssetDto;
  }

  async createMediaTile(
    customerId: string,
    projectId: string,
    sceneId: string,
    tile: MediaTileDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || !tile.ID) {
      throw new Error(
        "[HttpStorageModel] createMediaTile: customerId + projectId + sceneId + tile.ID required",
      );
    }
    const client = await this.getClient(customerId);
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: mediaTileDtoToItem(customerId, projectId, sceneId, tile),
      }),
      { abortSignal: signal },
    );
  }

  async createMediaAsset(
    customerId: string,
    projectId: string,
    asset: MediaAssetDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !asset.ID) {
      throw new Error(
        "[HttpStorageModel] createMediaAsset: customerId + projectId + asset.ID required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `ToMediaAssetItem`: ID S, MediaType N, Text S (default ""),
    // ImageId S (default ""), ActionDataId S (default ""), ModifiedTS S.
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: {
          ...marshall(
            {
              ID: asset.ID,
              MediaType: Number(asset.MediaType ?? 0),
              Text: asset.Text ?? "",
              ImageId: asset.ImageId ?? "",
              ActionDataId: asset.ActionDataId ?? "",
              ModifiedTS: asset.ModifiedTS ?? new Date().toISOString(),
            },
            { removeUndefinedValues: true },
          ),
          pk: { S: `${customerId}:project` },
          sk: { S: mediaAssetSk(projectId, asset.ID) },
        },
      }),
      { abortSignal: signal },
    );
  }

  async updateMediaTiles(
    customerId: string,
    projectId: string,
    sceneId: string,
    tiles: ReadonlyArray<MediaTileDto>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || tiles.length === 0) return;
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateMediaTiles` — full replace put per tile
    // (Unity batches via BatchWrite; per-item puts keep error surfaces simple).
    await Promise.all(
      tiles.map((tile) =>
        client.send(
          new PutItemCommand({
            TableName: this.m_config.projectTableName,
            Item: mediaTileDtoToItem(customerId, projectId, sceneId, tile),
          }),
          { abortSignal: signal },
        ),
      ),
    );
  }

  async deleteMediaTiles(
    customerId: string,
    projectId: string,
    sceneId: string,
    tileIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId || tileIds.length === 0) return;
    const client = await this.getClient(customerId);
    await Promise.all(
      tileIds.map((tileId) =>
        client.send(
          new DeleteItemCommand({
            TableName: this.m_config.projectTableName,
            Key: {
              pk: { S: `${customerId}:project` },
              sk: { S: `$project:${projectId}:scene:${sceneId}:mediatile:${tileId}` },
            },
          }),
          { abortSignal: signal },
        ),
      ),
    );
  }

  async deleteMediaAssets(
    customerId: string,
    projectId: string,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || assetIds.length === 0) return;
    const client = await this.getClient(customerId);

    // Unity's `DeleteMediaAssets` mistakenly doubles the separator
    // (`$project:{p}::mediaasset:{id}`) so its deletes never match a row.
    // We write the canonical key so the asset row actually goes away.
    await Promise.all(
      assetIds.map((assetId) =>
        client.send(
          new DeleteItemCommand({
            TableName: this.m_config.projectTableName,
            Key: {
              pk: { S: `${customerId}:project` },
              sk: { S: mediaAssetSk(projectId, assetId) },
            },
          }),
          { abortSignal: signal },
        ),
      ),
    );
  }

  async updateMediaAssetText(
    customerId: string,
    projectId: string,
    mediaAssetId: string,
    text: string,
    actionDataId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !mediaAssetId) {
      throw new Error(
        "[HttpStorageModel] updateMediaAssetText: customerId + projectId + mediaAssetId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateMediaAssetText`: preserve the linked
    // action id whenever card/button copy changes.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: mediaAssetSk(projectId, mediaAssetId) },
        },
        ExpressionAttributeNames: {
          "#T": "Text",
          "#A": "ActionDataId",
        },
        ExpressionAttributeValues: {
          ":t": { S: text ?? "" },
          ":a": { S: actionDataId ?? "" },
        },
        UpdateExpression: "SET #T = :t, #A = :a",
      }),
      { abortSignal: signal },
    );
  }

  async updateMediaAssetImage(
    customerId: string,
    projectId: string,
    mediaAssetId: string,
    imageId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !mediaAssetId) {
      throw new Error(
        "[HttpStorageModel] updateMediaAssetImage: customerId + projectId + mediaAssetId required",
      );
    }
    const client = await this.getClient(customerId);
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: mediaAssetSk(projectId, mediaAssetId) },
        },
        ExpressionAttributeNames: { "#I": "ImageId", "#MT": "ModifiedTS" },
        ExpressionAttributeValues: {
          ":i": { S: imageId },
          ":ts": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #I = :i, #MT = :ts",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneMediaAssetIDs(
    customerId: string,
    projectId: string,
    sceneId: string,
    mediaAssetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !sceneId) {
      throw new Error(
        "[HttpStorageModel] updateSceneMediaAssetIDs: customerId + projectId + sceneId required",
      );
    }
    const client = await this.getClient(customerId);

    // Mirrors `DynamoClient.UpdateSceneMediaAssetIDs` (`DynamoClient.cs:1928`).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: {
          "#M": "MediaAssetIDs",
          "#MT": "ModifiedTimestamp",
        },
        ExpressionAttributeValues: {
          ":m": stringListOrNull(mediaAssetIds),
          ":ts": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #M = :m, #MT = :ts",
      }),
      { abortSignal: signal },
    );
  }

  async getTextureBlobUrl(
    customerId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !textureId) return null;
    const s3 = await this.getS3Client();

    // `PathTemplates.k_texturePathTemplate` — "textures/{textureID}" under
    // the customer projectdata prefix (customer-level, not project-scoped).
    const key = `account/${customerId}/projectdata/textures/${textureId}`;
    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
        }),
        { abortSignal: signal },
      );
      const body = out.Body;
      if (!body) return null;
      const blob = await new Response(body as BodyInit).blob();
      return URL.createObjectURL(
        out.ContentType ? new Blob([blob], { type: out.ContentType }) : blob,
      );
    } catch (err) {
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      if (status === 404 || status === 403) return null;
      console.warn(`[HttpStorageModel] S3 GetObject failed for ${key}:`, err);
      return null;
    }
  }

  async putTextureObject(
    customerId: string,
    textureId: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !textureId) {
      throw new Error(
        "[HttpStorageModel] putTextureObject: customerId + textureId required",
      );
    }
    // See `putAssetObject`: send a Uint8Array so the browser S3 client doesn't
    // route the Blob through aws-chunked streaming (which throws).
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const s3 = await this.getS3Client();
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/textures/${textureId}`,
        Body: bytes,
        ContentType: blob.type || undefined,
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  async deleteTextureObject(
    customerId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !textureId) return;
    const s3 = await this.getS3Client();
    await s3.send(
      new DeleteObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/textures/${textureId}`,
      }),
      { abortSignal: signal },
    );
  }

  async getMaterialBatch(
    customerId: string,
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MaterialDto>> {
    const ids = [...new Set(materialIds.filter(Boolean))];
    if (!customerId || ids.length === 0) return [];
    const client = await this.getClient(customerId);

    const materials: MaterialDto[] = [];
    for (let offset = 0; offset < ids.length; offset += 100) {
      let requestKeys: Record<string, AttributeValue>[] = ids.slice(offset, offset + 100).map((materialId) => ({
        pk: { S: `${customerId}:material` },
        sk: { S: `$material:${materialId}` },
      }));
      do {
        const out = await client.send(
          new BatchGetItemCommand({
            RequestItems: {
              [this.m_config.projectTableName]: {
                Keys: requestKeys,
              },
            },
          }),
          { abortSignal: signal },
        );
        for (const item of out.Responses?.[this.m_config.projectTableName] ?? []) {
          materials.push(unmarshall(item) as MaterialDto);
        }
        requestKeys = out.UnprocessedKeys?.[this.m_config.projectTableName]?.Keys ?? [];
      } while (requestKeys.length > 0);
    }

    return materials;
  }

  async updateMaterial(
    customerId: string,
    _projectId: string,
    material: MaterialDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !material.ID) {
      throw new Error("[HttpStorageModel] updateMaterial: customerId + material.ID required");
    }
    const client = await this.getClient(customerId);
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: materialDtoToDynamoItem(customerId, material),
      }),
      { abortSignal: signal },
    );
  }

  async getAssetBlobUrl(
    customerId: string,
    objectKey: string,
    signal?: AbortSignal,
    onProgress?: TransferProgress,
  ): Promise<{ url: string; contentType: string | null } | null> {
    if (!customerId || !objectKey) return null;
    const s3 = await this.getS3Client();

    // Mirrors `AwsDataStorageModel.ObjectKeyForCustomer` +
    // `AwsDataStorageModel.GetFile`:
    //   account/{customerId}/projectdata/{objectKey}
    const key = `account/${customerId}/projectdata/${objectKey}`;

    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
        }),
        { abortSignal: signal },
      );
      const body = out.Body;
      if (!body) {
        console.warn(`[HttpStorageModel] S3 GetObject empty Body for ${key}`);
        return null;
      }

      // Scene GLBs run to hundreds of MB, so the caller gets the transfer
      // byte-by-byte rather than one step at the end. `ContentLength` is a
      // modeled `GetObjectOutput` member bound to the CORS-safelisted
      // `Content-Length` header, so this needs no bucket-CORS change; when
      // it is absent the caller is told `null` and falls back to
      // indeterminate rather than dividing by it.
      const counted = onProgress
        ? countingStream(body.transformToWebStream(), out.ContentLength, onProgress)
        : null;
      const blob = await new Response(counted ?? (body as BodyInit)).blob();
      const contentType = out.ContentType ?? null;
      const url = URL.createObjectURL(
        contentType ? new Blob([blob], { type: contentType }) : blob,
      );
      return { url, contentType };
    } catch (err) {
      if (err instanceof NoSuchKey) {
        console.warn(`[HttpStorageModel] S3 NoSuchKey for ${key}`);
        return null;
      }
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      // Treat 403/404 as "missing" but log loud so credential issues
      // don't disappear silently — most real-world failures here are
      // either an Identity-Pool role missing s3:GetObject permission or
      // the wrong bucket name in `cloudConfig.projectBucket`.
      console.warn(
        `[HttpStorageModel] S3 GetObject failed for ${key} (status=${status ?? "?"}):`,
        err,
      );
      return null;
    }
  }

  async getProjectThumbnailBlobUrl(
    customerId: string,
    projectId: string,
    thumbnailId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !projectId || !thumbnailId) return null;
    const s3 = await this.getS3Client();

    // Full S3 key mirrors `AwsDataStorageModel.ObjectKeyForCustomer` +
    // `GetStoragePathForThumbnail`:
    //   account/{customerId}/projectdata/{projectId}/thumbnails/{thumbnailId}
    // No `.png` suffix on the S3 key — only the local cache adds one
    // (Unity's `GetCachePathForThumbnail`).
    const key = `account/${customerId}/projectdata/${projectId}/thumbnails/${thumbnailId}`;

    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
          // Every capture overwrites this key; without it Firefox serves its
          // heuristically cached copy and the card shows the old image.
          ResponseCacheControl: "no-cache",
        }),
        { abortSignal: signal },
      );
      const body = out.Body;
      if (!body) return null;

      // `Body` is a `ReadableStream` (browser) / SDK helper. Wrap it
      // in a `Response` so we get a `Blob` regardless of the runtime
      // shape; `URL.createObjectURL` takes it from there. Caller
      // owns lifecycle (revoke when done).
      const blob = await new Response(body as BodyInit).blob();
      return URL.createObjectURL(
        out.ContentType
          ? new Blob([blob], { type: out.ContentType })
          : blob,
      );
    } catch (err) {
      // 404 / NoSuchKey is expected when the chosen `thumbnailId`
      // doesn't exist (instance-id miss, never-rendered scene). The
      // composable layer retries with the legacy raw-`sceneId` key
      // before giving up — same fallback Unity does.
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      if (status === 404 || status === 403) return null;
      console.warn(
        `[HttpStorageModel] S3 GetObject failed for ${key}:`,
        err,
      );
      return null;
    }
  }

  async getCustomerProject(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ProjectDto | null> {
    if (!customerId || !projectId) return null;
    const client = await this.getClient(customerId);
    const command = new GetItemCommand({
      TableName: this.m_config.projectTableName,
      Key: {
        pk: { S: `${customerId}:project` },
        // `!project:` prefix — same sk as every other project-row op
        // (Unity `DynamoClient.cs:273`; a bare `project:` key never matches).
        sk: { S: `!project:${projectId}` },
      },
    });
    const out = await client.send(command, { abortSignal: signal });
    if (!out.Item) return null;
    return toProjectDto(out.Item);
  }

  async getThumbnailAsync(
    _sceneId: string,
    _signal?: AbortSignal,
  ): Promise<ThumbnailBlob | null> {
    // Real S3 fetch lands with the project / scene thumbnail work
    // (M6 follow-up). The Dynamo project list is the higher-leverage
    // surface to ship first, so this stays a stub for one more turn.
    return null;
  }

  async putThumbnailObject(
    customerId: string,
    projectId: string,
    thumbnailId: string,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !thumbnailId) {
      throw new Error(
        "[HttpStorageModel] putThumbnailObject: customerId + projectId + thumbnailId required",
      );
    }
    const s3 = await this.getS3Client();
    // Same key template as `getProjectThumbnailBlobUrl` / `deleteThumbnailObject`
    // — no `.png` suffix (Unity only adds it for its LOCAL cache file).
    const key = `account/${customerId}/projectdata/${projectId}/thumbnails/${thumbnailId}`;
    // Uint8Array body (never Blob) — the browser S3 client routes Blob bodies
    // through aws-chunked streaming which throws (see `putAssetObject`).
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: key,
        Body: bytes,
        ContentType: "image/png",
      }),
      { abortSignal: signal },
    );
  }

  async updateSceneModifiedTimestamp(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    const modifiedAt = new Date().toISOString();
    const client = await this.getClient(customerId);
    // Mirrors `DynamoClient.UpdateSceneModifiedTimestamp` (:2267).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `#project:${projectId}:scene:${sceneId}` },
        },
        ExpressionAttributeNames: { "#MT": "ModifiedTimestamp" },
        ExpressionAttributeValues: { ":ts": { S: modifiedAt } },
        UpdateExpression: "SET #MT = :ts",
      }),
      { abortSignal: signal },
    );
    return { modifiedAt };
  }

  async updateCustomerProjectModifiedDate(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    const modifiedAt = new Date().toISOString();
    const client = await this.getClient(customerId);
    // Mirrors `DynamoClient.UpdateCustomerProjectModifiedDate` (:407), plus
    // the web-only last-editor stamp.
    const stamp = this.stampModifiedBy(
      { "#MT": "ModifiedTimestamp" },
      { ":ts": { S: modifiedAt } },
      "SET #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        UpdateExpression: stamp.setClause,
      }),
      { abortSignal: signal },
    );
    return { modifiedAt };
  }

  // ---- Workflow document -----------------------------------------

  async getWorkflowYaml(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !projectId) return null;
    const s3 = await this.getS3Client();

    const key = `account/${customerId}/projectdata/${projectId}/workflow.yaml`;
    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
          // The blob is MUTABLE at a fixed key and S3 sends no Cache-Control,
          // so the browser heuristically caches the GET body — a later read
          // after an overwrite can serve the OLD workflow (observed live).
          // This response override makes S3 answer with Cache-Control:
          // no-cache, keeping the browser from reusing it.
          ResponseCacheControl: "no-cache",
        }),
        { abortSignal: signal },
      );
      const body = out.Body;
      if (!body) return null;
      return await new Response(body as BodyInit).text();
    } catch (err) {
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      if (status === 404 || status === 403) return null;
      // A transient failure THROWS (unlike the asset read paths): the caller
      // must be able to tell "no workflow" from "could not check", or a later
      // create path would template-overwrite a real cloud file.
      console.warn(`[HttpStorageModel] S3 GetObject failed for ${key}:`, err);
      throw err;
    }
  }

  async putWorkflowYaml(
    customerId: string,
    projectId: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId) {
      throw new Error("[HttpStorageModel] putWorkflowYaml: customerId + projectId required");
    }
    const s3 = await this.getS3Client();
    const key = `account/${customerId}/projectdata/${projectId}/workflow.yaml`;
    // Uint8Array body (NOT a Blob — the browser S3 client routes Blob bodies
    // through aws-chunked streaming which throws; see putAssetObject). UTF-8
    // without BOM.
    const bytes = new TextEncoder().encode(text);
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: key,
        Body: bytes,
        ContentType: "application/yaml; charset=utf-8",
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  async putWorkflowSummary(
    customerId: string,
    summary: WorkflowSummaryDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !summary.workflow_id) {
      throw new Error(
        "[HttpStorageModel] putWorkflowSummary: customerId + workflow_id required",
      );
    }

    const client = await this.getClient(customerId);
    const item = marshall(
      {
        pk: `${customerId}:workflow`,
        sk: `!workflow:${summary.workflow_id}`,
        ...summary,
      },
      { removeUndefinedValues: true },
    );
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: item,
      }),
      { abortSignal: signal },
    );
  }

  async deleteWorkflowYaml(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId) return;
    await this.deleteObjectTolerant(
      `account/${customerId}/projectdata/${projectId}/workflow.yaml`,
      signal,
    );
  }

  async deleteWorkflowSummary(
    customerId: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !workflowId) return;
    const client = await this.getClient(customerId);
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: marshall({
          pk: `${customerId}:workflow`,
          sk: `!workflow:${workflowId}`,
        }),
      }),
      { abortSignal: signal },
    );
  }

  async putWorkflowProductReference(
    customerId: string,
    reference: WorkflowProductReferenceInput,
    signal?: AbortSignal,
  ): Promise<void> {
    const productNameKey = normalizeProductNameKey(reference.productNameKey || reference.productName);
    const workflowId = reference.workflowId.trim();
    if (!customerId || !productNameKey || !workflowId) {
      throw new Error(
        "[HttpStorageModel] putWorkflowProductReference: customerId + productNameKey + workflowId required",
      );
    }

    const now = new Date().toISOString();
    const names: Record<string, string> = {
      "#entityType": "entityType",
      "#productName": "productName",
      "#productNameKey": "productNameKey",
      "#workflowId": "workflowId",
      "#firstReferencedAt": "firstReferencedAt",
      "#lastValidatedAt": "lastValidatedAt",
    };
    const values: Record<string, AttributeValue> = {
      ":entityType": { S: WORKFLOW_PRODUCT_REFERENCE_ENTITY_TYPE },
      ":productName": { S: reference.productName.trim() || productNameKey },
      ":productNameKey": { S: productNameKey },
      ":workflowId": { S: workflowId },
      ":now": { S: now },
    };
    const setParts = [
      "#entityType = :entityType",
      "#productName = :productName",
      "#productNameKey = :productNameKey",
      "#workflowId = :workflowId",
      "#firstReferencedAt = if_not_exists(#firstReferencedAt, :now)",
      "#lastValidatedAt = :now",
    ];
    const removeParts: string[] = [];

    const workflowName = reference.workflowName?.trim() ?? "";
    if (workflowName) {
      names["#workflowName"] = "workflowName";
      values[":workflowName"] = { S: workflowName };
      setParts.push("#workflowName = :workflowName");
    } else {
      names["#workflowName"] = "workflowName";
      removeParts.push("#workflowName");
    }

    const workflowVersion = reference.workflowVersion?.trim() ?? "";
    if (workflowVersion) {
      names["#workflowVersion"] = "workflowVersion";
      values[":workflowVersion"] = { S: workflowVersion };
      setParts.push("#workflowVersion = :workflowVersion");
    } else {
      names["#workflowVersion"] = "workflowVersion";
      removeParts.push("#workflowVersion");
    }

    const validatedBy = reference.validatedBy?.trim() ?? "";
    if (validatedBy) {
      names["#validatedBy"] = "validatedBy";
      values[":validatedBy"] = { S: validatedBy };
      setParts.push("#validatedBy = :validatedBy");
    } else {
      names["#validatedBy"] = "validatedBy";
      removeParts.push("#validatedBy");
    }

    const updateExpression = `SET ${setParts.join(", ")}${
      removeParts.length > 0 ? ` REMOVE ${removeParts.join(", ")}` : ""
    }`;

    const client = await this.getClient(customerId);
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: marshall({
          pk: workflowByProductPk(customerId, productNameKey),
          sk: workflowProductReferenceSk(workflowId),
        }),
        UpdateExpression: updateExpression,
        ExpressionAttributeNames: names,
        ExpressionAttributeValues: values,
      }),
      { abortSignal: signal },
    );
  }

  async deleteWorkflowProductReference(
    customerId: string,
    productNameKey: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const normalizedProduct = normalizeProductNameKey(productNameKey);
    const normalizedWorkflowId = workflowId.trim();
    if (!customerId || !normalizedProduct || !normalizedWorkflowId) return;

    const client = await this.getClient(customerId);
    await client.send(
      new DeleteItemCommand({
        TableName: this.m_config.projectTableName,
        Key: marshall({
          pk: workflowByProductPk(customerId, normalizedProduct),
          sk: workflowProductReferenceSk(normalizedWorkflowId),
        }),
      }),
      { abortSignal: signal },
    );
  }

  // ---- Documents (library docs + AI memory) ------------------------
  //
  // Customer-scoped keys under `account/{customerId}/projectdata/`, mirroring
  // Unity's generic `GetBytes/PutBytes/DeleteFile(customerId, storagePath)`.

  async listDocumentsByProductName(
    customerId: string,
    productName: string,
    options?: { readonly includeArchived?: boolean },
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ProductDocumentDto>> {
    const productKey = normalizeProductNameKey(productName);
    if (!customerId || !productKey) return [];

    const client = await this.getClient(customerId);
    const links: ProductDocumentLinkDto[] = [];
    let lastKey: Record<string, AttributeValue> | undefined;
    do {
      const out = await client.send(
        new QueryCommand({
          TableName: this.m_config.projectTableName,
          KeyConditionExpression: "pk = :v_pk and begins_with(sk, :v_sk)",
          ExpressionAttributeValues: {
            ":v_pk": { S: documentByProductPk(customerId, productKey) },
            ":v_sk": { S: "!document:" },
          },
          ExclusiveStartKey: lastKey,
        }),
        { abortSignal: signal },
      );
      for (const item of out.Items ?? []) {
        const link = unmarshall(item) as ProductDocumentLinkDto;
        const entityType = stringField(link, "entityType") ?? stringField(link, "EntityType");
        if (entityType && entityType !== PRODUCT_DOCUMENT_LINK_ENTITY_TYPE) continue;
        if (!options?.includeArchived && isProductDocumentLinkArchived(link)) continue;
        links.push(link);
      }
      lastKey = out.LastEvaluatedKey;
    } while (lastKey);

    const keys = new Map<string, ProductDocumentKey>();
    for (const link of links) {
      const pk = stringField(link, "documentPk") ?? stringField(link, "DocumentPk") ?? canonicalDocumentPk(customerId);
      const sk =
        stringField(link, "documentSk")
        ?? stringField(link, "DocumentSk")
        ?? canonicalDocumentSk(documentNameKeyFromSk(stringField(link, "sk") ?? stringField(link, "SK")));
      if (!pk || !sk || sk === "!document:") continue;
      keys.set(`${pk}\u0000${sk}`, { pk, sk });
    }

    const documents: ProductDocumentDto[] = [];
    const allKeys = Array.from(keys.values());
    for (let offset = 0; offset < allKeys.length; offset += 100) {
      let requestKeys: Record<string, AttributeValue>[] = allKeys.slice(offset, offset + 100).map((key) => ({
        pk: { S: key.pk },
        sk: { S: key.sk },
      }));
      do {
        const out = await client.send(
          new BatchGetItemCommand({
            RequestItems: {
              [this.m_config.projectTableName]: {
                Keys: requestKeys,
              },
            },
          }),
          { abortSignal: signal },
        );
        for (const item of out.Responses?.[this.m_config.projectTableName] ?? []) {
          const document = unmarshall(item) as ProductDocumentDto;
          if (!options?.includeArchived && isProductDocumentArchived(document)) continue;
          documents.push(document);
        }
        requestKeys = out.UnprocessedKeys?.[this.m_config.projectTableName]?.Keys ?? [];
      } while (requestKeys.length > 0);
    }

    return documents;
  }

  async getDocumentsByNameKeys(
    customerId: string,
    documentNameKeys: ReadonlyArray<string>,
    options?: { readonly includeArchived?: boolean },
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ProductDocumentDto>> {
    if (!customerId || documentNameKeys.length === 0) return [];

    const seen = new Set<string>();
    const keys = documentNameKeys
      .map((key) => normalizeProductNameKey(key))
      .filter((key) => {
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((key): ProductDocumentKey => ({
        pk: canonicalDocumentPk(customerId),
        sk: canonicalDocumentSk(key),
      }));
    if (keys.length === 0) return [];

    const client = await this.getClient(customerId);
    const documents: ProductDocumentDto[] = [];
    for (let offset = 0; offset < keys.length; offset += 100) {
      let requestKeys: Record<string, AttributeValue>[] = keys.slice(offset, offset + 100).map((key) => ({
        pk: { S: key.pk },
        sk: { S: key.sk },
      }));
      do {
        const out = await client.send(
          new BatchGetItemCommand({
            RequestItems: {
              [this.m_config.projectTableName]: {
                Keys: requestKeys,
              },
            },
          }),
          { abortSignal: signal },
        );
        for (const item of out.Responses?.[this.m_config.projectTableName] ?? []) {
          const document = unmarshall(item) as ProductDocumentDto;
          if (!options?.includeArchived && isProductDocumentArchived(document)) continue;
          documents.push(document);
        }
        requestKeys = out.UnprocessedKeys?.[this.m_config.projectTableName]?.Keys ?? [];
      } while (requestKeys.length > 0);
    }

    return documents;
  }

  async getProductByName(
    customerId: string,
    productName: string,
    signal?: AbortSignal,
  ): Promise<ProductDto | null> {
    const productNameKey = normalizeProductNameKey(productName);
    if (!customerId || !productNameKey) return null;

    const client = await this.getClient(customerId);
    const out = await client.send(
      new GetItemCommand({
        TableName: this.m_config.projectTableName,
        Key: marshall({
          pk: productPk(customerId),
          sk: productSk(productNameKey),
        }),
      }),
      { abortSignal: signal },
    );
    return out.Item ? (unmarshall(out.Item) as ProductDto) : null;
  }

  async getDocsManifestJson(customerId: string, signal?: AbortSignal): Promise<string | null> {
    return this.getCustomerTextObject(customerId, DOCS_MANIFEST_STORAGE_PATH, signal);
  }

  async putDocsManifestJson(
    customerId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.putCustomerTextObject(
      customerId,
      DOCS_MANIFEST_STORAGE_PATH,
      json,
      "application/json; charset=utf-8",
      signal,
    );
  }

  async getVectorStoreSettingsJson(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    return this.getCustomerTextObject(customerId, VECTOR_STORE_SETTINGS_STORAGE_PATH, signal);
  }

  async putVectorStoreSettingsJson(
    customerId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.putCustomerTextObject(
      customerId,
      VECTOR_STORE_SETTINGS_STORAGE_PATH,
      json,
      "application/json; charset=utf-8",
      signal,
    );
  }

  async getWorkflowVectorManifestJson(
    customerId: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    return this.getCustomerTextObject(customerId, workflowVectorManifestStoragePath(workflowId), signal);
  }

  async putWorkflowVectorManifestJson(
    customerId: string,
    workflowId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.putCustomerTextObject(
      customerId,
      workflowVectorManifestStoragePath(workflowId),
      json,
      "application/json; charset=utf-8",
      signal,
    );
  }

  // ---- Media library (workflow step media) --------------------------

  async getMediaLibraryManifestJson(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    return this.getCustomerTextObject(customerId, MEDIA_LIBRARY_MANIFEST_STORAGE_PATH, signal);
  }

  async putMediaLibraryManifestJson(
    customerId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.putCustomerTextObject(
      customerId,
      MEDIA_LIBRARY_MANIFEST_STORAGE_PATH,
      json,
      "application/json; charset=utf-8",
      signal,
    );
  }

  async putMediaLibraryObject(
    customerId: string,
    storagePath: string,
    bytes: Uint8Array,
    contentType: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !storagePath) {
      throw new Error("[HttpStorageModel] putMediaLibraryObject: customerId + storagePath required");
    }
    const s3 = await this.getS3Client();
    // Uint8Array body (NOT a Blob — see `putAssetObject`).
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/${storagePath}`,
        Body: bytes,
        ContentType: contentType,
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  async getMediaLibraryBlobUrl(
    customerId: string,
    storagePath: string,
    contentType?: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    return this.getDocBlobUrl(customerId, storagePath, contentType, signal);
  }

  async deleteMediaLibraryObject(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !storagePath) return;
    await this.deleteObjectTolerant(`account/${customerId}/projectdata/${storagePath}`, signal);
  }

  // ---- Shared application settings ---------------------------------
  //
  // REMOVED, deliberately: `get/putAppSettingsJson` and
  // `get/putAgentPromptsJson` used to resolve to
  // `account/{customerId}/projectdata/settings/…`. That prefix is the only tree
  // a browser can write, and it — not any property of the data — is why the
  // DEPLOYMENT's agent configuration was stored per customer. Neither document
  // is customer data: two accounts on one deployment must not run two different
  // agents. Both moved to the host that serves the app, behind
  // `IDeploymentConfigModel` (`models/config/`, served by
  // `deploy/config-service/`). Do not re-add a customer-scoped path for them.

  async putDocObject(
    customerId: string,
    storagePath: string,
    bytes: Uint8Array,
    contentType: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !storagePath) {
      throw new Error("[HttpStorageModel] putDocObject: customerId + storagePath required");
    }
    const s3 = await this.getS3Client();
    // Uint8Array body (NOT a Blob — see `putAssetObject`).
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/${storagePath}`,
        Body: bytes,
        ContentType: contentType,
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  /**
   * The S3 keys a document's stored path could name, most-likely first.
   *
   * A Define-authored document's `storagePath` is derived from the row's
   * `fileKey` (`productDocumentStoragePath`), which is URL-PATH shaped — a
   * document titled "Robodog Manual" arrives as `docs/robodog%20manual.pdf`.
   * Nothing in the port decodes it, and the SDK re-encodes whatever it is
   * handed; measured against the shipped `@aws-sdk/client-s3`:
   *
   *   docs/robodog%20manual.pdf  ->  .../docs/robodog%2520manual.pdf
   *   docs/robodog manual.pdf    ->  .../docs/robodog%20manual.pdf
   *
   * ⚠ Those are two DIFFERENT objects, and which one exists depends on what
   * the Define app passed to its own SDK when it uploaded — a fact this repo
   * cannot see. Re-encoding is symmetric, so either form could be the real
   * key. Hence: the stored path is tried EXACTLY as written first, so a
   * document that opens today cannot regress by one byte, and the decoded
   * form is a retry paid only on the failure path.
   *
   * ⚠ SHARED by three readers, not one: `getDocBlobUrl` (the citation chip
   * and `Find_and_Show_PDF`), `getDocBytes` (`CustomerVectorStoreModel`'s
   * sync, where a wrong object would poison retrieval for every later
   * question), and `getMediaLibraryBlobUrl`, which delegates straight to
   * `getDocBlobUrl`. A media path reaches the retry only if the author's own
   * file name contains a `%` — `mediaLibraryStoragePath` stores the LITERAL
   * name and `parseMediaLibraryUri` has already decoded the URI — and
   * `uniqueMediaFileName`'s random suffix makes a literal-`%20` file colliding
   * with a real-space file effectively impossible. So it is one wasted round
   * trip on a read that was failing anyway, never a wrong object.
   *
   * This is deliberately NOT a `docs/{fileName}` fallback — `fileName` is not
   * unique across documents (`documentDisplayNames.ts`), so that one can open
   * the wrong document's bytes and report success. Decoding re-spells the same
   * path rather than naming a different document.
   *
   * ⚠ THAT IS ONLY TRUE WHILE THE DECODE CANNOT CHANGE THE KEY'S SHAPE, and it
   * can: `%2F` decodes to a separator, so `docs/a%2Fb.pdf` becomes
   * `docs/a/b.pdf` (a different depth) and `docs/%2E%2E%2Fx` becomes
   * `docs/../x` (an escape from the prefix — dots are unreserved, so the SDK
   * does not re-escape them, and only the URL parser's own normalisation
   * stands between that and a read outside the document space). The retry is
   * therefore refused whenever the decoded form has a different segment count
   * or contains a `..` segment. Neither is reachable from a Define `fileKey`
   * today; the guard is what keeps that from being load-bearing.
   */
  private docObjectKeys(customerId: string, storagePath: string): string[] {
    const prefix = `account/${customerId}/projectdata/`;
    const keys = [`${prefix}${storagePath}`];
    if (storagePath.includes("%")) {
      try {
        const decoded = decodeURIComponent(storagePath);
        // A RE-SPELLING only. An encoded separator would change the key's
        // shape rather than its spelling, so both are refused: a different
        // segment count is a different location, and a `..` segment leaves the
        // document space entirely.
        if (
          decoded !== storagePath &&
          !decoded.split("/").includes("..") &&
          decoded.split("/").length === storagePath.split("/").length
        ) {
          keys.push(`${prefix}${decoded}`);
        }
      } catch {
        // A stray `%` is a literal, not an escape — there is nothing to retry.
      }
    }
    return keys;
  }

  private async getDocObject(
    customerId: string,
    storagePath: string,
    what: string,
    signal?: AbortSignal,
  ): Promise<GetObjectCommandOutput | null> {
    const keys = this.docObjectKeys(customerId, storagePath);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const isLast = i === keys.length - 1;
      try {
        const s3 = await this.getS3Client();
        return await s3.send(
          new GetObjectCommand({ Bucket: this.m_config.projectBucket, Key: key }),
          { abortSignal: signal },
        );
      } catch (err) {
        // ⚠ CLASSIFY, do not just report. A document that will not open has
        // four candidate causes — the key's encoding, a wrong prefix, a
        // cross-account grant, a blocked preflight — and a bare "failed"
        // separates none of them. The STATUS cannot either: without
        // `s3:ListBucket` S3 answers a MISSING key with 403, not 404 (see
        // `deleteObjectTolerant`). The error NAME can — NoSuchKey/NotFound is
        // the key, AccessDenied is the grant, and no `$metadata` at all is the
        // network (CORS / preflight). Read as `err.name` rather than
        // `instanceof NoSuchKey`: the sibling at the asset reader early-returns
        // on that type, which here would skip the retry this loop exists for.
        const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
          ?.httpStatusCode;
        const name = err instanceof Error ? err.name : "?";
        const why = `${name}, status=${status ?? "?"}`;
        if (isLast || signal?.aborted) {
          console.warn(`[HttpStorageModel] S3 ${what} failed for ${key} (${why}):`, err);
          return null;
        }
        console.warn(
          `[HttpStorageModel] S3 ${what} missed ${key} (${why}) — retrying the decoded key:`,
          err,
        );
      }
    }
    return null;
  }

  async getDocBlobUrl(
    customerId: string,
    storagePath: string,
    contentType?: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !storagePath) return null;
    const out = await this.getDocObject(customerId, storagePath, "GetObject", signal);
    if (!out?.Body) return null;
    const bytes = await new Response(out.Body as BodyInit).arrayBuffer();
    // Prefer the caller's guess over S3's stored type: Unity's `PutFile`
    // sets no ContentType, so every Unity-uploaded doc reads back as
    // `binary/octet-stream` and a PDF would download instead of rendering.
    const type = contentType || out.ContentType || "application/octet-stream";
    return URL.createObjectURL(new Blob([bytes], { type }));
  }

  async getDocBytes(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<{ bytes: Uint8Array; contentType: string | null } | null> {
    if (!customerId || !storagePath) return null;
    const out = await this.getDocObject(customerId, storagePath, "GetObject bytes", signal);
    if (!out?.Body) return null;
    const buffer = await new Response(out.Body as BodyInit).arrayBuffer();
    return {
      bytes: new Uint8Array(buffer),
      contentType: out.ContentType ?? null,
    };
  }

  async deleteDocObject(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !storagePath) return;
    await this.deleteObjectTolerant(
      `account/${customerId}/projectdata/${storagePath}`,
      signal,
    );
  }

  async getDocThumbnailBlobUrl(
    customerId: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !fileName) return null;
    const key = `account/${customerId}/projectdata/${docThumbnailStoragePath(fileName)}`;
    try {
      const s3 = await this.getS3Client();
      const out = await s3.send(
        new GetObjectCommand({ Bucket: this.m_config.projectBucket, Key: key }),
        { abortSignal: signal },
      );
      if (!out.Body) return null;
      const bytes = await new Response(out.Body as BodyInit).arrayBuffer();
      return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
    } catch {
      // Absent is the norm — only Unity-uploaded PDFs have one, and S3
      // answers a missing key with 403 (not 404) without `s3:ListBucket`.
      // Unity swallows NotFound/Forbidden the same way.
      return null;
    }
  }

  async deleteDocThumbnailObject(
    customerId: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !fileName) return;
    await this.deleteObjectTolerant(
      `account/${customerId}/projectdata/${docThumbnailStoragePath(fileName)}`,
      signal,
    );
  }

  async updateCustomerProjectDocumentStoragePaths(
    customerId: string,
    projectId: string,
    storagePaths: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }> {
    if (!customerId || !projectId) {
      throw new Error(
        "[HttpStorageModel] updateCustomerProjectDocumentStoragePaths: customerId + projectId required",
      );
    }
    const client = await this.getClient(customerId);
    const modifiedAt = new Date().toISOString();
    // Mirrors `DynamoClient.UpdateCustomerProjectDocumentStoragePaths` (:318):
    // `SET #DSP = :new, #MT = :ts`, empty list → Dynamo NULL (same convention
    // as `updateCustomerProjectAssetIDs`).
    const stamp = this.stampModifiedBy(
      {
        "#DSP": "DocumentStoragePaths",
        "#MT": "ModifiedTimestamp",
      },
      {
        ":new":
          storagePaths.length > 0
            ? { L: storagePaths.map((p) => ({ S: p })) }
            : { NULL: true },
        ":ts": { S: modifiedAt },
      },
      "SET #DSP = :new, #MT = :ts",
    );
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: `!project:${projectId}` },
        },
        ExpressionAttributeNames: stamp.names,
        ExpressionAttributeValues: stamp.values,
        UpdateExpression: stamp.setClause,
      }),
      { abortSignal: signal },
    );
    return { modifiedAt };
  }

  /**
   * Shared read for the two customer-scoped JSON blobs (docs manifest,
   * vector-store settings). Both are MUTABLE at a fixed key, so the
   * `ResponseCacheControl` override from `getWorkflowYaml` applies: without
   * it the browser heuristically caches the GET body and a read after an
   * overwrite serves the OLD document (observed live with `workflow.yaml`).
   *
   * Missing (404/403/NoSuchKey) → `null`; anything else THROWS, so a
   * transient failure can never be mistaken for "no manifest" by a caller
   * about to write a replacement.
   */
  private async getCustomerTextObject(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !storagePath) return null;
    const s3 = await this.getS3Client();
    const key = `account/${customerId}/projectdata/${storagePath}`;
    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
          ResponseCacheControl: "no-cache",
        }),
        { abortSignal: signal },
      );
      if (!out.Body) return null;
      return await new Response(out.Body as BodyInit).text();
    } catch (err) {
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      if (status === 404 || status === 403) return null;
      console.warn(`[HttpStorageModel] S3 GetObject failed for ${key}:`, err);
      throw err;
    }
  }

  private async putCustomerTextObject(
    customerId: string,
    storagePath: string,
    text: string,
    contentType: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !storagePath) {
      throw new Error("[HttpStorageModel] putCustomerTextObject: customerId + storagePath required");
    }
    const s3 = await this.getS3Client();
    const bytes = new TextEncoder().encode(text);
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/${storagePath}`,
        Body: bytes,
        ContentType: contentType,
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  // ---- Annotations (comments) ------------------------------------

  async getAnnotations(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<AnnotationQueryResult> {
    if (!customerId || !projectId) return { annotations: [], replies: [] };
    const client = await this.getClient(customerId);

    // `DynamoClient.GetAnnotations` (:3431): one `sk between` query over the
    // `annotation:{MinGuid}`..`{MaxGuid}` window. Reply rows sort within that
    // window (their sk is the annotation sk + `:reply:{id}`), so this returns
    // annotations AND replies; we split by the `:reply:` segment and keep the
    // raw `sk` to recover each reply's parent id.
    const pk = `${customerId}:project`;
    const skStart = annotationSk(projectId, MIN_GUID);
    const skEnd = annotationSk(projectId, MAX_GUID);

    const rawItems: Record<string, AttributeValue>[] = [];
    let lastKey: Record<string, AttributeValue> | undefined;
    do {
      const out = await client.send(
        new QueryCommand({
          TableName: this.m_config.projectTableName,
          KeyConditionExpression: "pk = :v_pk and sk between :v_start and :v_end",
          ExpressionAttributeValues: {
            ":v_pk": { S: pk },
            ":v_start": { S: skStart },
            ":v_end": { S: skEnd },
          },
          ExclusiveStartKey: lastKey,
        }),
        { abortSignal: signal },
      );
      if (out.Items) rawItems.push(...out.Items);
      lastKey = out.LastEvaluatedKey;
    } while (lastKey);

    const annotations: AnnotationDto[] = [];
    const replies: AnnotationReplyDto[] = [];
    for (const item of rawItems) {
      const sk = item.sk?.S ?? "";
      if (sk.includes(ANNOTATION_REPLY_SK_SEGMENT)) {
        const reply = unmarshall(item) as AnnotationReplyDto;
        replies.push({
          ...reply,
          AnnotationId: parseAnnotationIdFromReplySk(sk) ?? undefined,
        });
      } else {
        annotations.push(unmarshall(item) as AnnotationDto);
      }
    }
    return { annotations, replies };
  }

  async createAnnotation(
    customerId: string,
    projectId: string,
    annotation: AnnotationDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotation?.ID) return;
    const client = await this.getClient(customerId);

    // `ToAnnotationItem` (:3343), new format. Detached text comment → the
    // AttachmentPosition/PlacementPosition/PlacementRotation maps and the
    // ParentEntitySnapshotIds/Forces/PosVecs lists are omitted (Unity's
    // `SafeSet*` skips empties); `SketchUploaded` is not written on create.
    const cleaned: Record<string, unknown> = {
      AnnotationType: annotation.AnnotationType ?? 0,
      IsResolved: annotation.IsResolved ?? 0,
      IsSceneVisible: annotation.IsSceneVisible ?? true,
      Fov: annotation.Fov ?? 60,
      OrthographicSize: annotation.OrthographicSize ?? 0,
      AnnotationText: annotation.AnnotationText ?? "",
      ParentEntitySnapshotId: annotation.ParentEntitySnapshotId ?? "",
      CreatorId: annotation.CreatorId ?? "",
      CreatorNaturalAccountId: annotation.CreatorNaturalAccountId ?? "",
      CreatedTimestamp: annotation.CreatedTimestamp,
      ModifiedTimestamp: annotation.ModifiedTimestamp,
      SceneID: annotation.SceneID ?? "",
      CreatorInitials: annotation.CreatorInitials ?? "",
      CreatorName: annotation.CreatorName ?? "",
      ID: annotation.ID,
      VisualizationType: annotation.VisualizationType ?? 0,
      ActionDataId: annotation.ActionDataId ?? "",
      isExplosionAnnotation: annotation.isExplosionAnnotation ?? false,
    };
    // Marker geometry (Dynamo `M`): only for scene-placed annotations. Empty
    // maps are omitted (Unity `SafeSetVectorAttribute` skip-empty).
    if (annotation.AttachmentPosition) cleaned.AttachmentPosition = annotation.AttachmentPosition;
    if (annotation.PlacementPosition) cleaned.PlacementPosition = annotation.PlacementPosition;
    if (annotation.PlacementRotation) cleaned.PlacementRotation = annotation.PlacementRotation;
    const marshalled = marshall(cleaned, {
      removeUndefinedValues: true,
      convertClassInstanceToMap: true,
    });

    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: {
          ...marshalled,
          pk: { S: `${customerId}:project` },
          sk: { S: annotationSk(projectId, annotation.ID) },
        },
      }),
      { abortSignal: signal },
    );
  }

  async updateAnnotationPlacement(
    customerId: string,
    projectId: string,
    annotationId: string,
    placementPosition: { x: number; y: number; z: number },
    placementRotation: { x: number; y: number; z: number; w: number },
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId) return;
    const client = await this.getClient(customerId);
    // Mirrors Unity's `UpdateAnnotationFlags.PlacementPose` path
    // (`UpdateAnnotationPlacementPosition`:3532 + `...Rotation`:3547), folded
    // into one `UpdateItem`: SET PlacementPosition (M) + PlacementRotation (M)
    // + ModifiedTimestamp. The anchor (AttachmentPosition) is never touched on
    // drag — only placement moves.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationSk(projectId, annotationId) },
        },
        ExpressionAttributeNames: {
          "#PP": "PlacementPosition",
          "#PR": "PlacementRotation",
          "#MT": "ModifiedTimestamp",
        },
        ExpressionAttributeValues: {
          ":pp": { M: marshall(placementPosition) },
          ":pr": { M: marshall(placementRotation) },
          ":mt": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #PP = :pp, #PR = :pr, #MT = :mt",
      }),
      { abortSignal: signal },
    );
  }

  async updateAnnotationText(
    customerId: string,
    projectId: string,
    annotationId: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId) return;
    const client = await this.getClient(customerId);
    // `UpdateAnnotationText` (:3517) — the shared update also bumps
    // `ModifiedTimestamp` (`CreateAnnotationUpdateRequest`:3493).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationSk(projectId, annotationId) },
        },
        ExpressionAttributeNames: { "#N": "AnnotationText", "#MT": "ModifiedTimestamp" },
        ExpressionAttributeValues: {
          ":new": { S: text },
          ":mt": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #N = :new, #MT = :mt",
      }),
      { abortSignal: signal },
    );
  }

  async resolveAnnotation(
    customerId: string,
    projectId: string,
    annotationId: string,
    resolvedStatus: number,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId) return;
    const client = await this.getClient(customerId);
    // `ResolveAnnotation` (:3592) — `IsResolved` is an int `N` (0/1).
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationSk(projectId, annotationId) },
        },
        ExpressionAttributeNames: { "#N": "IsResolved", "#MT": "ModifiedTimestamp" },
        ExpressionAttributeValues: {
          ":new": { N: String(Math.trunc(resolvedStatus)) },
          ":mt": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #N = :new, #MT = :mt",
      }),
      { abortSignal: signal },
    );
  }

  async updateAnnotationSketchUploaded(
    customerId: string,
    projectId: string,
    annotationId: string,
    sketchUploaded: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId) return;
    const client = await this.getClient(customerId);
    // `UpdateAnnotationSketchUploaded` (:3584) — shared annotation update shape
    // (`SET #N = :new, #MT = :mt`), `SketchUploaded` is a BOOL.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationSk(projectId, annotationId) },
        },
        ExpressionAttributeNames: { "#N": "SketchUploaded", "#MT": "ModifiedTimestamp" },
        ExpressionAttributeValues: {
          ":new": { BOOL: sketchUploaded },
          ":mt": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #N = :new, #MT = :mt",
      }),
      { abortSignal: signal },
    );
  }

  async getSketchBlobUrl(
    customerId: string,
    sketchId: string,
    kind: SketchBlobKind,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!customerId || !sketchId) return null;
    const s3 = await this.getS3Client();
    const key = `account/${customerId}/projectdata/${sketchStoragePath(sketchId, kind)}`;
    try {
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
        }),
        { abortSignal: signal },
      );
      const body = out.Body;
      if (!body) return null;
      const blob = await new Response(body as BodyInit).blob();
      return URL.createObjectURL(new Blob([blob], { type: "image/png" }));
    } catch (err) {
      // Missing layers are expected (old-format sketches have no -bg.png) —
      // Unity's `LogAndContinueIfNotFound` parity.
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      if (status === 404 || status === 403) return null;
      console.warn(`[HttpStorageModel] S3 GetObject failed for ${key}:`, err);
      return null;
    }
  }

  async putSketchObject(
    customerId: string,
    sketchId: string,
    kind: SketchBlobKind,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !sketchId) {
      throw new Error("[HttpStorageModel] putSketchObject: customerId + sketchId required");
    }
    const s3 = await this.getS3Client();
    const key = `account/${customerId}/projectdata/${sketchStoragePath(sketchId, kind)}`;
    // Uint8Array body (never Blob) — the browser S3 client routes Blob bodies
    // through aws-chunked streaming which throws (see `putAssetObject`).
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: key,
        Body: bytes,
        ContentType: "image/png",
      }),
      { abortSignal: signal },
    );
  }

  async deleteAnnotations(
    customerId: string,
    projectId: string,
    annotationIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || annotationIds.length === 0) return;
    const client = await this.getClient(customerId);
    // `DeleteAnnotations` (:3608) — batched key-only deletes of the annotation
    // rows (reply rows are swept separately via `deleteAnnotationReplies`).
    const requests: WriteRequest[] = annotationIds.map((id) => ({
      DeleteRequest: {
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationSk(projectId, id) },
        },
      },
    }));
    await this.batchWrite(client, requests, signal);
  }

  async createAnnotationReply(
    customerId: string,
    projectId: string,
    annotationId: string,
    reply: AnnotationReplyDto,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId || !reply?.ID) return;
    const client = await this.getClient(customerId);
    // `ToAnnotationReplyItem` (:3649) — reply text is `Message`, timestamps
    // are `CreatedTS`/`ModifiedTS` (NOT `…Timestamp`).
    const cleaned: Record<string, unknown> = {
      ID: reply.ID,
      Message: reply.Message ?? "",
      CreatedTS: reply.CreatedTS,
      ModifiedTS: reply.ModifiedTS,
      CreatorId: reply.CreatorId ?? "",
      CreatorInitials: reply.CreatorInitials ?? "",
      CreatorName: reply.CreatorName ?? "",
      CreatorNaturalAccountId: reply.CreatorNaturalAccountId ?? "",
    };
    const marshalled = marshall(cleaned, {
      removeUndefinedValues: true,
      convertClassInstanceToMap: true,
    });
    await client.send(
      new PutItemCommand({
        TableName: this.m_config.projectTableName,
        Item: {
          ...marshalled,
          pk: { S: `${customerId}:project` },
          sk: { S: annotationReplySk(projectId, annotationId, reply.ID) },
        },
      }),
      { abortSignal: signal },
    );
  }

  async updateAnnotationReplyText(
    customerId: string,
    projectId: string,
    annotationId: string,
    replyId: string,
    message: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId || !replyId) return;
    const client = await this.getClient(customerId);
    // `UpdateAnnotationReplyText` (:3697). Unity's writer sets `ModifiedTimestamp`
    // (a bug — the row is read via `ModifiedTS`), so the visible mod-time never
    // changes there. We write `ModifiedTS` so the edit actually updates the
    // mod-time both readers consume.
    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationReplySk(projectId, annotationId, replyId) },
        },
        ExpressionAttributeNames: { "#N": "Message", "#MT": "ModifiedTS" },
        ExpressionAttributeValues: {
          ":new": { S: message },
          ":mt": { S: new Date().toISOString() },
        },
        UpdateExpression: "SET #N = :new, #MT = :mt",
      }),
      { abortSignal: signal },
    );
  }

  async deleteAnnotationReplies(
    customerId: string,
    projectId: string,
    annotationId: string,
    replyIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!customerId || !projectId || !annotationId || replyIds.length === 0) return;
    const client = await this.getClient(customerId);
    // `DeleteAnnotationReplies` (:3732) — batched key-only deletes.
    const requests: WriteRequest[] = replyIds.map((replyId) => ({
      DeleteRequest: {
        Key: {
          pk: { S: `${customerId}:project` },
          sk: { S: annotationReplySk(projectId, annotationId, replyId) },
        },
      },
    }));
    await this.batchWrite(client, requests, signal);
  }

  resetCloudEnvironment(): void {
    this.m_client?.destroy();
    this.m_s3?.destroy();
    this.m_client = null;
    this.m_s3 = null;
    this.m_clientCustomerId = null;
  }

  // -- Internals -------------------------------------------------

  /**
   * Generic paginated `Query` helper for `pk = :pk and sk between :s
   * and :e`. Used by every range-by-sort-key endpoint
   * (`getEntitySnapshots`, `getEntitySettings`, `getParts`, scenes,
   * etc — those still inline the call for tighter logging today).
   * Unmarshalls each item into the caller-chosen DTO; pass-through
   * `[k: string]: unknown` on every DTO keeps it round-trip safe.
   */
  private async queryAll<T>(
    client: DynamoDBClient,
    pk: string,
    skStart: string,
    skEnd: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<T>> {
    const items: Record<string, AttributeValue>[] = [];
    let lastKey: Record<string, AttributeValue> | undefined;
    do {
      const command = new QueryCommand({
        TableName: this.m_config.projectTableName,
        KeyConditionExpression: "pk = :v_pk and sk between :v_start and :v_end",
        ExpressionAttributeValues: {
          ":v_pk": { S: pk },
          ":v_start": { S: skStart },
          ":v_end": { S: skEnd },
        },
        ExclusiveStartKey: lastKey,
      });
      const out = await client.send(command, { abortSignal: signal });
      if (out.Items) items.push(...out.Items);
      lastKey = out.LastEvaluatedKey;
    } while (lastKey);
    return items.map((item) => unmarshall(item) as T);
  }

  private async updateProjectRow(
    client: DynamoDBClient,
    pk: string,
    sk: string,
    fields: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<void> {
    const update = buildDynamoSetExpression(fields);
    if (!update) return;

    await client.send(
      new UpdateItemCommand({
        TableName: this.m_config.projectTableName,
        Key: {
          pk: { S: pk },
          sk: { S: sk },
        },
        ExpressionAttributeNames: update.names,
        ExpressionAttributeValues: update.values,
        UpdateExpression: update.expression,
      }),
      { abortSignal: signal },
    );
  }


  /**
   * Read one Unity V2 project-data object. Missing/forbidden keys mean the
   * project is V1 or partially migrated; malformed payloads still throw so a
   * corrupt V2 scene cannot silently masquerade as an empty scene.
   */
  private async getProjectStorageJson(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<unknown | null> {
    const key = `account/${customerId}/projectdata/${storagePath}`;
    try {
      const s3 = await this.getS3Client();
      const out = await s3.send(
        new GetObjectCommand({
          Bucket: this.m_config.projectBucket,
          Key: key,
          // The desktop client rewrites these files in place (deletes and
          // creates), so a cached body would resurrect or hide snapshots.
          ResponseCacheControl: "no-cache",
        }),
        { abortSignal: signal },
      );
      if (!out.Body) {
        throw new Error(`[HttpStorageModel] S3 GetObject returned no body for ${key}`);
      }
      const buffer = await new Response(out.Body as BodyInit).arrayBuffer();
      return decodeProjectStorageJson(new Uint8Array(buffer));
    } catch (err) {
      if (err instanceof NoSuchKey) return null;
      const status = (err as { $metadata?: { httpStatusCode?: number } })?.$metadata
        ?.httpStatusCode;
      // Cognito roles without ListBucket commonly receive 403 for a missing
      // object, which Unity treats as NotFound in its fallback paths.
      if (status === 404 || status === 403) return null;
      throw err;
    }
  }

  private async putProjectStorageJson(
    customerId: string,
    storagePath: string,
    value: unknown,
    signal?: AbortSignal,
  ): Promise<void> {
    const bytes = encodeProjectStorageJson(value);
    const s3 = await this.getS3Client();
    await s3.send(
      new PutObjectCommand({
        Bucket: this.m_config.projectBucket,
        Key: `account/${customerId}/projectdata/${storagePath}`,
        Body: bytes,
        ContentType: "application/octet-stream",
        ContentLength: bytes.byteLength,
      }),
      { abortSignal: signal },
    );
  }

  /**
   * Port of Unity `StorageModel.QueueEntitySnapshotFileChanges`. Resolves once
   * a write containing these changes has landed. The write is shared by every
   * caller folded into it, so no caller's abort signal is passed to it.
   */
  private queueEntitySnapshotFileChanges(
    customerId: string,
    projectId: string,
    sceneId: string,
    upserts: ReadonlyMap<string, JsonRecord>,
    deletes: ReadonlyArray<string>,
  ): Promise<void> {
    const storagePath = entitySnapshotsStoragePath(projectId, sceneId);
    const key = `${customerId}|${storagePath}`;
    let queue = this.m_snapshotFileQueues.get(key);
    if (!queue) {
      queue = { upserts: new Map(), deletes: new Set(), waiters: [], writing: false };
      this.m_snapshotFileQueues.set(key, queue);
    }
    for (const [id, row] of upserts) {
      queue.deletes.delete(id);
      queue.upserts.set(id, row);
    }
    for (const id of deletes) {
      queue.upserts.delete(id);
      queue.deletes.add(id);
    }
    const done = new Promise<void>((resolve, reject) => {
      queue.waiters.push({ resolve, reject });
    });
    if (!queue.writing) void this.drainEntitySnapshotFileQueue(key, customerId, storagePath, queue);
    return done;
  }

  private async drainEntitySnapshotFileQueue(
    key: string,
    customerId: string,
    storagePath: string,
    queue: SnapshotFileQueue,
  ): Promise<void> {
    queue.writing = true;
    try {
      while (queue.waiters.length > 0) {
        const upserts = queue.upserts;
        const deletes = queue.deletes;
        const waiters = queue.waiters;
        queue.upserts = new Map();
        queue.deletes = new Set();
        queue.waiters = [];
        try {
          const existing = await this.getProjectStorageJson(customerId, storagePath);
          const merged = mergeEntitySnapshotFile(existing, upserts, deletes);
          if (merged) await this.putProjectStorageJson(customerId, storagePath, merged);
          for (const waiter of waiters) waiter.resolve();
        } catch (err) {
          for (const waiter of waiters) waiter.reject(err);
        }
      }
    } finally {
      queue.writing = false;
      if (this.m_snapshotFileQueues.get(key) === queue) this.m_snapshotFileQueues.delete(key);
    }
  }

  /**
   * One V2 override write per scene at a time: each write reads the current
   * override before replacing it, so two overlapping patches to one snapshot
   * would otherwise drop the first one's fields.
   */
  private enqueueV2SnapshotOverrideWrite(
    customerId: string,
    projectId: string,
    sceneId: string,
    write: () => Promise<void>,
  ): Promise<void> {
    const key = `${customerId}|${projectId}|${sceneId}`;
    const previous = this.m_v2OverrideWriteChains.get(key) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(write);
    const tail = next.catch(() => undefined);
    this.m_v2OverrideWriteChains.set(key, tail);
    void tail.then(() => {
      if (this.m_v2OverrideWriteChains.get(key) === tail) this.m_v2OverrideWriteChains.delete(key);
    });
    return next;
  }

  /** Port of Unity's V2 `UpdateEntitySnapshots`: whole-row `PutItem` overrides. */
  private async writeV2SnapshotOverrides(
    customerId: string,
    projectId: string,
    sceneId: string,
    updates: ReadonlyArray<{ readonly snapshotId: string; readonly patch: EntitySnapshotPatch }>,
    signal?: AbortSignal,
  ): Promise<void> {
    const patches = new Map<string, Record<string, unknown>>();
    for (const update of updates) {
      if (!update.snapshotId) {
        throw new Error("[HttpStorageModel] updateEntitySnapshots: snapshotId required");
      }
      const fields = snapshotPatchToDynamoFields(update.patch);
      if (Object.keys(fields).length === 0) continue;
      patches.set(update.snapshotId, { ...patches.get(update.snapshotId), ...fields });
    }
    if (patches.size === 0) return;

    const storagePath = entitySnapshotsStoragePath(projectId, sceneId);
    // A placement still being merged into the file must land before its rows are read back.
    if (this.m_snapshotFileQueues.has(`${customerId}|${storagePath}`)) {
      await this.queueEntitySnapshotFileChanges(customerId, projectId, sceneId, new Map(), []);
    }
    const client = await this.getClient(customerId);
    const pk = `${customerId}:project`;
    const skPrefix = `$project:${projectId}:scene:${sceneId}:snapshot:`;
    const [payload, overrides] = await Promise.all([
      this.getProjectStorageJson(customerId, storagePath, signal),
      this.batchGetProjectRowsConsistent(
        client,
        [...patches.keys()].map((id) => ({ pk, sk: `${skPrefix}${id}` })),
        signal,
      ),
    ]);
    const fileRows = new Map<string, EntitySnapshotDto>();
    for (const row of payload === null ? [] : snapshotsFromV2Payload(payload)) {
      if (row.SnapshotID) fileRows.set(row.SnapshotID, row);
    }

    const writes: WriteRequest[] = [];
    for (const [snapshotId, fields] of patches) {
      const fileRow = fileRows.get(snapshotId);
      const override = overrides.get(`${skPrefix}${snapshotId}`) as EntitySnapshotDto | undefined;
      const [current] = mergeEntitySnapshots(fileRow ? [fileRow] : [], override ? [override] : []);
      if (!current?.EntityID) {
        console.warn(
          `[HttpStorageModel] updateEntitySnapshots: snapshot ${snapshotId} is in neither `
          + `${storagePath} nor its overrides; patch not persisted.`,
        );
        continue;
      }
      // A file row still carries its raw lower-camel JSON keys beside the mapped
      // DTO fields; only the DTO fields belong on a Dynamo row.
      const dtoFields = Object.fromEntries(
        Object.entries(current).filter(([key]) => !/^[a-z]/.test(key)),
      ) as EntitySnapshotDto;
      const next: EntitySnapshotDto = {
        ...dtoFields,
        ...fields,
        SnapshotID: snapshotId,
        EntityID: current.EntityID,
        HasMergedChildren:
          fields.Merged !== undefined ? fields.Merged === true : current.HasMergedChildren,
      };
      writes.push({ PutRequest: { Item: snapshotDtoToItem(customerId, projectId, sceneId, next) } });
    }
    if (writes.length > 0) await this.batchWrite(client, writes, signal);
  }

  private async batchGetProjectRowsConsistent(
    client: DynamoDBClient,
    keys: ReadonlyArray<{ pk: string; sk: string }>,
    signal?: AbortSignal,
  ): Promise<Map<string, Record<string, unknown>>> {
    const table = this.m_config.projectTableName;
    const rows = new Map<string, Record<string, unknown>>();
    for (let offset = 0; offset < keys.length; offset += K_DYNAMO_BATCH_GET_MAX_KEYS) {
      let pending: Array<Record<string, AttributeValue>> = keys
        .slice(offset, offset + K_DYNAMO_BATCH_GET_MAX_KEYS)
        .map((key) => ({ pk: { S: key.pk }, sk: { S: key.sk } }));
      for (let attempt = 0; attempt < 5 && pending.length > 0; attempt++) {
        const out = await client.send(
          new BatchGetItemCommand({
            RequestItems: { [table]: { Keys: pending, ConsistentRead: true } },
          }),
          { abortSignal: signal },
        );
        for (const item of out.Responses?.[table] ?? []) {
          const row = unmarshall(item);
          if (typeof row.sk === "string") rows.set(row.sk, row);
        }
        pending = out.UnprocessedKeys?.[table]?.Keys ?? [];
      }
      if (pending.length > 0) {
        throw new Error(
          `[HttpStorageModel] batchGetProjectRowsConsistent: ${pending.length} keys unprocessed after retries`,
        );
      }
    }
    return rows;
  }

  /**
   * Lazily build (and reuse) a `DynamoDBClient` for the current
   * Cognito identity. Re-created when the customer changes (sign-out
   * / sign-in switch) so stale temporary credentials don't leak
   * across users.
   */
  private async getClient(customerId: string): Promise<DynamoDBClient> {
    if (this.m_client && this.m_clientCustomerId === customerId) {
      return this.m_client;
    }
    this.m_client?.destroy();

    this.m_client = new DynamoDBClient({
      region: this.m_config.region,
      // Provider closure: every signed request asks Amplify for the
      // current AWS keys, so SDK retries automatically pick up
      // refreshed tokens without us reaching into Amplify state.
      credentials: async () => {
        const session = await fetchAuthSession();
        const creds = session.credentials;
        if (!creds || !creds.accessKeyId || !creds.secretAccessKey) {
          throw new Error(
            "[HttpStorageModel] No AWS credentials available — sign in first.",
          );
        }
        return {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          sessionToken: creds.sessionToken,
          expiration: creds.expiration,
        };
      },
    });
    this.m_clientCustomerId = customerId;
    return this.m_client;
  }

  /**
   * Lazy S3 client. Shares the same Cognito Identity-Pool credential
   * provider as the Dynamo client — Unity's `AwsHelpers.
   * GetAwsCredentialsAndRegionEndpoint` does the same thing
   * (`AwsDataStorageModel.cs:108-117`, the non-proxy branch).
   */
  private async getS3Client(): Promise<S3Client> {
    if (this.m_s3) return this.m_s3;
    this.m_s3 = new S3Client({
      region: this.m_config.region,
      credentials: async () => {
        const session = await fetchAuthSession();
        const creds = session.credentials;
        if (!creds || !creds.accessKeyId || !creds.secretAccessKey) {
          throw new Error(
            "[HttpStorageModel] No AWS credentials available — sign in first.",
          );
        }
        return {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          sessionToken: creds.sessionToken,
          expiration: creds.expiration,
        };
      },
    });
    return this.m_s3;
  }
}

function toProjectDto(item: Record<string, AttributeValue>): ProjectDto {
  // `unmarshall` drops Dynamo's `pk` / `sk` envelope keys naturally
  // because they are not declared on `ProjectDto`; they survive in the
  // pass-through `[extra: string]: unknown` index signature for
  // debugging / round-trip safety.
  return unmarshall(item) as ProjectDto;
}

function materialDtoToDynamoItem(
  customerId: string,
  material: MaterialDto,
): Record<string, AttributeValue> {
  const cleaned = {
    ...material,
    pk: `${customerId}:material`,
    sk: `$material:${material.ID}`,
    Type: "material",
    LastModified: new Date().toISOString(),
  };
  return marshall(cleaned, {
    removeUndefinedValues: true,
    convertClassInstanceToMap: true,
  });
}

/**
 * Wraps a response body so every chunk that passes through is counted and
 * pushed to `onProgress` — the web equivalent of Unity reporting
 * `destStream.Position` while `S3Client.CopyStream` runs
 * (`Models/StorageModel/AWS/S3Client.cs:93`).
 *
 * `contentLength` comes straight off `GetObjectOutput`; anything that is not
 * a positive finite number becomes a `null` total, which the convention on
 * {@link TransferProgress} defines as "indeterminate". Fires once with
 * `(0, total)` up front so a consumer learns the denominator before the
 * first chunk lands, and never throttles — that is the consumer's call,
 * since only it knows what its own reports cost.
 */
function countingStream(
  stream: ReadableStream<Uint8Array>,
  contentLength: number | undefined,
  onProgress: TransferProgress,
): ReadableStream<Uint8Array> {
  const total =
    typeof contentLength === "number" && Number.isFinite(contentLength) && contentLength > 0
      ? contentLength
      : null;
  let loaded = 0;
  onProgress(0, total);
  return stream.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        loaded += chunk.byteLength;
        onProgress(loaded, total);
        controller.enqueue(chunk);
      },
    }),
  );
}

function buildDynamoSetExpression(
  fields: Record<string, unknown>,
): {
  readonly names: Record<string, string>;
  readonly values: Record<string, AttributeValue>;
  readonly expression: string;
} | null {
  const entries = Object.entries(marshall(fields, {
    removeUndefinedValues: true,
    convertClassInstanceToMap: true,
  }));
  if (entries.length === 0) return null;

  const names: Record<string, string> = {};
  const values: Record<string, AttributeValue> = {};
  const sets: string[] = [];
  entries.forEach(([field, value], index) => {
    const nameKey = `#f${index}`;
    const valueKey = `:v${index}`;
    names[nameKey] = field;
    values[valueKey] = value;
    sets.push(`${nameKey} = ${valueKey}`);
  });
  return { names, values, expression: `SET ${sets.join(", ")}` };
}

function buildProjectRowTransactionUpdate(
  tableName: string,
  pk: string,
  sk: string,
  fields: Record<string, unknown>,
): TransactWriteItem | null {
  const update = buildDynamoSetExpression(fields);
  if (!update) return null;
  return {
    Update: {
      TableName: tableName,
      Key: { pk: { S: pk }, sk: { S: sk } },
      ExpressionAttributeNames: update.names,
      ExpressionAttributeValues: update.values,
      UpdateExpression: update.expression,
    },
  };
}

function snapshotPatchToDynamoFields(
  patch: EntitySnapshotPatch,
): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  if (patch.name !== undefined) fields.Name = patch.name;
  if (patch.displayName !== undefined) fields.DisplayName = patch.displayName;
  if (patch.visibility !== undefined) fields.Visibility = patch.visibility;
  if (patch.forcedVisibility !== undefined) fields.ForcedVisibility = patch.forcedVisibility;
  if (patch.isLocked !== undefined) fields.IsLock = patch.isLocked;
  // Attribute name is `Merged`, not the C# member name (`DynamoClient.cs:2669`).
  if (patch.hasMergedChildren !== undefined) fields.Merged = patch.hasMergedChildren;
  if (patch.explosionFactor !== undefined) fields.ExplosionFactor = patch.explosionFactor;
  if (patch.materialIds !== undefined) fields.MaterialIDs = [...patch.materialIds];
  if (patch.librarySyncedVersion !== undefined) {
    fields.LibrarySyncedVersion = patch.librarySyncedVersion;
  }
  if (patch.transform?.position) fields.Position = vec3ToNameFloatMap(patch.transform.position);
  if (patch.transform?.rotation) fields.Rotation = vec3ToNameFloatMap(patch.transform.rotation);
  if (patch.transform?.scale) fields.Scale = vec3ToNameFloatMap(patch.transform.scale);
  if (patch.persistentSpinJson !== undefined) {
    // Clear (`null`) writes "" rather than REMOVEing the attribute (Unity's
    // full-row put simply omits it) — both Unity's `PersistentSpinData.
    // Deserialize` and the web reader treat a blank string as "no spin".
    fields.PersistentSpinJson = patch.persistentSpinJson ?? "";
  }
  // PersistentHighlightJson is deliberately read-compatible but never
  // written by Web. Highlights are transient runtime presentation state.
  if (patch.aliases !== undefined) {
    // An EMPTY list is written rather than REMOVEd: Dynamo accepts an empty
    // `L` (unlike `SS`), and `readAliases` maps both an empty list and an
    // absent attribute to `[]`, so "cleared" and "never set" read the same.
    fields.Aliases = aliasesToDto(patch.aliases);
  }
  return fields;
}

function settingsPatchToDynamoFields(
  patch: EntitySettingsPatch,
): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  if (patch.name !== undefined) fields.Name = patch.name;
  if (patch.displayName !== undefined) fields.DisplayName = patch.displayName;
  if (patch.description !== undefined) fields.Description = patch.description;
  if (patch.flipNormals !== undefined) fields.FlipNormals = patch.flipNormals;
  if (patch.visualizePoints !== undefined) fields.VisualizePoints = patch.visualizePoints;
  if (patch.animation !== undefined) {
    if (patch.animation.autoplay !== undefined) {
      fields.AnimationAutoplay = patch.animation.autoplay;
    }
    if (patch.animation.playback !== undefined) {
      const playback = patch.animation.playback.trim().toLowerCase().replace(/[_ -]/g, "");
      fields.AnimationPlaybackType = playback === "once" ? 1 : playback === "pingpong" ? 2 : 0;
    }
    // ⚠ SPEED AND DIRECTION ARE ONE ATTRIBUTE. `AnimationSpeed` is a SIGNED
    // multiplier — direction is its sign, not a field of its own — so writing
    // it from `speedPercent` alone silently disagrees with the in-memory merge
    // in two ways: a `direction`-only patch produced NO field at all (the
    // inspector's own Direction dropdown has therefore never persisted), and a
    // `speedPercent`-only patch on a model playing backward wrote a POSITIVE
    // speed while local state kept the negative one.
    //
    // `ProjectModel.updateEntitySettingsAsync` resolves the merge against the
    // current settings and hands the resolved pair down, so both halves are
    // always present here when either was asked for.
    if (patch.animation.speedPercent !== undefined || patch.animation.direction !== undefined) {
      const direction = patch.animation.direction?.trim().toLowerCase();
      const magnitude = patch.animation.speedPercent !== undefined
        ? Math.abs(patch.animation.speedPercent * 0.01)
        : undefined;
      if (magnitude !== undefined) {
        fields.AnimationSpeed = magnitude * (direction === "backward" ? -1 : 1);
      }
    }
    if (patch.animation.take !== undefined && patch.animation.take.trim().length > 0) {
      const clipIndex = Number.parseInt(patch.animation.take, 10);
      if (Number.isFinite(clipIndex)) fields.AnimationClipIndex = clipIndex;
    }
  }
  if (patch.kinematics !== undefined) fields.KinematicsSettings = patch.kinematics;
  if (patch.compatibilityVariants !== undefined) {
    fields.CompatibilityVariants = patch.compatibilityVariants;
  }
  return fields;
}

function vec3ToNameFloatMap(v: Vec3): Record<string, number> {
  return { x: v[0], y: v[1], z: v[2] };
}

function selectionSetSk(projectId: string, sceneId: string, selectionSetId: string): string {
  return `$project:${projectId}:scene:${sceneId}:selectionset:${selectionSetId}`;
}

function mediaAssetSk(projectId: string, mediaAssetId: string): string {
  return `$project:${projectId}:mediaasset:${mediaAssetId}`;
}

/**
 * Full media-tile item mirroring `DynamoClient.ToMediaTileItem`
 * (`DynamoClient.cs:4496-4546`) — attribute names and types match Unity
 * exactly so both clients read each other's rows.
 */
function mediaTileDtoToItem(
  customerId: string,
  projectId: string,
  sceneId: string,
  tile: MediaTileDto,
): Record<string, AttributeValue> {
  const position = tile.Position ?? {};
  const rotation = tile.Rotation ?? {};
  const bgColor = tile.BgColor ?? {};
  return {
    pk: { S: `${customerId}:project` },
    sk: { S: `$project:${projectId}:scene:${sceneId}:mediatile:${tile.ID}` },
    ID: { S: tile.ID },
    AssetID: { S: tile.AssetID ?? "" },
    Name: { S: tile.Name ?? "" },
    Visibility: { BOOL: tile.Visibility ?? true },
    BillboardFactor: { N: String(Number(tile.BillboardFactor ?? 1)) },
    Width: { N: String(Math.round(Number(tile.Width ?? 100))) },
    FixedWidth: { BOOL: tile.FixedWidth ?? false },
    Scale: { N: String(Number(tile.Scale ?? 1)) },
    Locked: { BOOL: tile.Locked ?? false },
    ForcedVisibility: { BOOL: tile.ForcedVisibility ?? false },
    ImagePlacement: { N: String(Math.round(Number(tile.ImagePlacement ?? 0))) },
    Position: vecAttribute(position, ["x", "y", "z"]),
    Rotation: vecAttribute(rotation, ["x", "y", "z"]),
    BgColor: vecAttribute(bgColor, ["r", "g", "b", "a"]),
  };
}

function vecAttribute(
  map: Readonly<Record<string, number>>,
  keys: ReadonlyArray<string>,
): AttributeValue {
  const out: Record<string, AttributeValue> = {};
  for (const key of keys) {
    out[key] = { N: String(Number(map[key] ?? 0)) };
  }
  return { M: out };
}

function stringField(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * Coerce a DTO vector into the `{axis: number}` shape `vecAttribute` reads.
 *
 * ⚠ Goes through `toNameFloatMap`, never a bare cast. A DTO can legitimately
 * carry a vector as JsonUtility's `NameFloatPair[]` (`[{name:"x",val:1}]`) —
 * that is what Unity's V2 `parts.json` / `entity-snapshots.json` hold — and a
 * cast indexes `.x` on an ARRAY, gets `undefined`, and `vecAttribute` then
 * writes `{0,0,0}` for every axis with nothing reported. That is exactly how
 * a library placement of a V2-saved asset persisted zero scale on every child
 * part (2026-09-03): correct on screen from memory, collapsed to a point on
 * the next reload. The V2 reader now folds vectors at its own boundary; this
 * is the belt for any other DTO that reaches a writer un-normalised.
 */
function asRecord(value: unknown): Record<string, number> {
  return toNameFloatMap(value);
}

const XYZ = ["x", "y", "z"] as const;

/**
 * Attributes `assetDtoToItem` writes explicitly. Everything else on the DTO is
 * carried through verbatim — see the note on {@link assetDtoToItem}.
 */
const K_ASSET_KNOWN_KEYS: ReadonlySet<string> = new Set([
  "ID",
  "Name",
  "FileName",
  "FilePath",
  "FileSize",
  "Provider",
  "Format",
  "Scale",
  "ObjectKey",
  "LastModified",
  "UserTag",
  "ImporterVersion",
  "AssetVersion",
  "TriangleCount",
  "Type",
  "pk",
  "sk",
]);

/**
 * Mirrors `DynamoClient.ToAssetItem` (`DynamoClient.cs:729`).
 *
 * This is a whole-item `PutItem`, so it is also the marshaller behind
 * `UpdateAsset` — anything it omits is DESTROYED on the row. Unity is safe
 * there because `AssetData` is a complete C# model; the web `AssetDto` types
 * only the fields it reads and keeps the rest in a passthrough bucket. So
 * unknown attributes (`HasLicensingRestrictions`, `Variants`, and whatever a
 * newer Unity build adds) are re-marshalled and merged back in, rather than
 * silently dropped the first time the web re-saves a Unity-authored asset.
 */
function assetDtoToItem(customerId: string, asset: AssetDto): Record<string, AttributeValue> {
  const passthrough: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(asset)) {
    if (K_ASSET_KNOWN_KEYS.has(key) || value === undefined) continue;
    passthrough[key] = value;
  }

  return {
    ...marshall(passthrough, { removeUndefinedValues: true }),
    pk: { S: `${customerId}:asset` },
    sk: { S: `!asset:${asset.ID}` },
    // Unity's `ToAssetItem` writes `AssetData.FileName` under the `Name`
    // attribute; `Name` on the DTO is the read-side spelling of the same value.
    Name: { S: asset.FileName ?? (typeof asset.Name === "string" ? asset.Name : "") },
    ID: { S: asset.ID },
    Type: { S: "asset" },
    FilePath: { S: asset.FilePath ?? "" },
    FileSize: { N: String(Math.max(0, Math.round(Number(asset.FileSize ?? 0)))) },
    Provider: { S: asset.Provider ?? "" },
    Format: { S: asset.Format ?? "GLB" },
    Scale: { N: String(Number(asset.Scale ?? 1)) },
    ObjectKey: {
      S: asset.ObjectKey ?? assetObjectKeyForFormat(asset.ID, asset.Format ?? null),
    },
    LastModified: { S: new Date().toISOString() },
    UserTag: { S: asset.UserTag ?? "" },
    ImporterVersion: { S: asset.ImporterVersion ?? "1.0.1" },
    AssetVersion: { N: String(Math.round(Number(asset.AssetVersion ?? 0))) },
    TriangleCount: { N: String(Math.max(0, Math.round(Number(asset.TriangleCount ?? 0)))) },
  };
}

/**
 * Mirrors `DynamoClient.ToPartItem` (`DynamoClient.cs:1109`).
 *
 * `Visibility` / `Merged` / `MaterialIDs` were hardcoded to `true` / `false` /
 * absent while the only caller was the import path, where every part is freshly
 * created visible, unmerged, and (web deviation D1) material-less. They now
 * carry the DTO's real values because `updateParts` bakes live scene state onto
 * these rows in Save to Library — a hidden or merged part, or one with library
 * materials, has to survive the round trip.
 *
 * `Description` follows Unity's `SafeSetStringAttribute` (omitted when empty)
 * so a part row's description isn't blanked by a save that never touched it.
 */
function partDtoToItem(
  customerId: string,
  assetId: string,
  part: PartDto,
): Record<string, AttributeValue> {
  const materialIds = partMaterialIds(part);
  const item: Record<string, AttributeValue> = {
    pk: { S: `${customerId}:asset` },
    sk: { S: `#asset:${assetId}:part:${part.ID}` },
    Name: { S: part.Name ?? "" },
    DisplayName: { S: part.DisplayName ?? part.Name ?? "" },
    Type: { S: "part" },
    ID: { S: part.ID },
    HierachyID: { S: part.HierachyID ?? "" },
    AssetID: { S: assetId },
    Visibility: { BOOL: part.Visibility ?? true },
    Merged: { BOOL: part.Merged ?? false },
    Position: vecAttribute(asRecord(part.Position), XYZ),
    Rotation: vecAttribute(asRecord(part.Rotation), XYZ),
    Scale: vecAttribute(asRecord(part.Scale), XYZ),
  };
  // Unity `SafeSetListOfStringsAttribute` / `SafeSetStringAttribute`: skip the
  // attribute entirely when there is nothing to write.
  if (materialIds.length > 0) {
    item.MaterialIDs = { L: materialIds.map((id) => ({ S: id })) };
  }
  if (typeof part.Description === "string" && part.Description.length > 0) {
    item.Description = { S: part.Description };
  }
  // Library part name aliases (web-first field — see `aliasData.ts`). Same
  // omit-when-empty rule as `MaterialIDs` above, so an asset that never had
  // aliases carries no attribute at all.
  const aliases = aliasesToDto(readAliases(part.Aliases));
  if (aliases.length > 0) {
    item.Aliases = {
      L: aliases.map((a) => ({ M: { Language: { S: a.Language ?? "" }, Value: { S: a.Value ?? "" } } })),
    };
  }
  return item;
}

/**
 * Attributes {@link snapshotDtoToItem} writes explicitly. Everything else on
 * the DTO is carried through verbatim — see the note on that function.
 *
 * `HasMergedChildren` is listed alongside `Merged` because the DTO's field
 * name and the Dynamo attribute name differ (Unity: `HasMergedChildren` in C#,
 * `"Merged"` on the row, `DynamoClient.cs:2593/2668`); excluding only one
 * spelling would let the passthrough write a phantom second attribute.
 */
const K_SNAPSHOT_KNOWN_KEYS: ReadonlySet<string> = new Set([
  "pk",
  "sk",
  "Type",
  "Name",
  "DisplayName",
  "SnapshotID",
  "PartID",
  "EntityID",
  "IsRoot",
  "IsLock",
  "IsPointVisible",
  "Visibility",
  "ForcedVisibility",
  "Merged",
  "HasMergedChildren",
  "ExplosionFactor",
  "IsCollisionEnabled",
  "IsGravityEnabled",
  "Position",
  "Rotation",
  "Scale",
  "InitialPos",
  "InitialRot",
  "AuthoringPos",
  "AuthoringRot",
  "MaterialIDs",
  // Read-compatible legacy/runtime field, deliberately excluded from every
  // Web whole-row PutItem as well as the patch writer above.
  "PersistentHighlightJson",
  "persistentHighlightJson",
  "Aliases",
  "LibrarySyncedVersion",
]);

/**
 * Mirrors `DynamoClient.ToEntitySnapshotItem` (`DynamoClient.cs:2603`).
 *
 * This is a whole-item `PutItem`, and two callers re-PUT rows that ALREADY
 * exist — `duplicateEntityAsync` (clones a live entity's rows) and Save to
 * Library's fork branch (re-points the entity at the forked asset). So, exactly
 * like {@link assetDtoToItem}, anything this marshaller omits is DESTROYED on
 * the row rather than merely not-written. Unity is safe there because
 * `EntitySnapshotData` is a complete C# model; the web `EntitySnapshotDto`
 * types only the fields it reads and keeps the rest in a passthrough bucket.
 * Unknown attributes are therefore re-marshalled and merged back in.
 *
 * Without that, a fork silently dropped `PersistentSpinJson` — a spinning part
 * stopped spinning the next time its model was saved to the library — and the
 * new `Aliases` list would have been lost the same way.
 */
function snapshotDtoToItem(
  customerId: string,
  projectId: string,
  sceneId: string,
  snap: EntitySnapshotDto,
): Record<string, AttributeValue> {
  const passthrough: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(snap)) {
    if (K_SNAPSHOT_KNOWN_KEYS.has(key) || value === undefined) continue;
    passthrough[key] = value;
  }

  const item: Record<string, AttributeValue> = {
    ...marshall(passthrough, { removeUndefinedValues: true }),
    pk: { S: `${customerId}:project` },
    sk: { S: `$project:${projectId}:scene:${sceneId}:snapshot:${snap.SnapshotID}` },
    Name: { S: snap.Name ?? "" },
    DisplayName: { S: snap.DisplayName ?? snap.Name ?? "" },
    Type: { S: "snapshot" },
    SnapshotID: { S: snap.SnapshotID ?? "" },
    PartID: { S: snap.PartID ?? "" },
    EntityID: { S: snap.EntityID },
    IsRoot: { BOOL: snap.IsRoot ?? false },
    IsLock: { BOOL: snap.IsLock ?? false },
    IsPointVisible: { BOOL: false },
    Visibility: { BOOL: snap.Visibility ?? true },
    ForcedVisibility: { BOOL: snap.ForcedVisibility ?? false },
    Merged: { BOOL: snap.HasMergedChildren ?? false },
    // assembled = 1 (round-trips to the same value the offline path derives
    // from a missing attribute, per `normalizeExplosionFactor`).
    ExplosionFactor: { N: String(Number(snap.ExplosionFactor ?? 1)) },
    IsCollisionEnabled: { BOOL: false },
    IsGravityEnabled: { BOOL: false },
    Position: vecAttribute(asRecord(snap.Position), XYZ),
    Rotation: vecAttribute(asRecord(snap.Rotation), XYZ),
    Scale: vecAttribute(asRecord(snap.Scale), XYZ),
    InitialPos: vecAttribute(asRecord(snap.InitialPos), XYZ),
    InitialRot: vecAttribute(asRecord(snap.InitialRot), XYZ),
  };
  const materialIds = snap.MaterialIDs ?? [];
  if (materialIds.length > 0) item.MaterialIDs = stringListOrNull(materialIds);
  // Part name aliases — omit-when-empty, same rule as `MaterialIDs`.
  const aliases = aliasesToDto(readAliases(snap.Aliases));
  if (aliases.length > 0) {
    item.Aliases = {
      L: aliases.map((a) => ({ M: { Language: { S: a.Language ?? "" }, Value: { S: a.Value ?? "" } } })),
    };
  }
  if (snap.IsRoot) {
    // Unity persists a separate authoring pose on the root snapshot; seed it
    // from the current pose so placement-reset has a baseline. A re-put of an
    // existing row (a V2 override) must keep the baseline it already has.
    const authoringPos = asRecord(snap.AuthoringPos);
    const authoringRot = asRecord(snap.AuthoringRot);
    item.AuthoringPos = vecAttribute(
      authoringPos && Object.keys(authoringPos).length > 0 ? authoringPos : asRecord(snap.Position),
      XYZ,
    );
    item.AuthoringRot = vecAttribute(
      authoringRot && Object.keys(authoringRot).length > 0 ? authoringRot : asRecord(snap.Rotation),
      XYZ,
    );
  }
  if (typeof snap.LibrarySyncedVersion === "number") {
    item.LibrarySyncedVersion = { N: String(snap.LibrarySyncedVersion) };
  }
  return item;
}

/** Mirrors `DynamoClient.ToEntitySettingsItem` (`DynamoClient.cs:2881`). */
function settingsDtoToItem(
  customerId: string,
  projectId: string,
  sceneId: string,
  settings: EntitySettingsDto,
): Record<string, AttributeValue> {
  return {
    pk: { S: `${customerId}:project` },
    sk: { S: `$project:${projectId}:scene:${sceneId}:entity:${settings.EntityID}` },
    EntityID: { S: settings.EntityID },
    // Unity stores DisplayOrder as a STRING (`entitySettings.DisplayOrder.ToString()`).
    DisplayOrder: { S: String(settings.DisplayOrder ?? 0) },
  };
}

function selectionSetForWrite(selectionSet: SelectionSetData): Record<string, unknown> {
  const out: Record<string, unknown> = {
    ...selectionSet,
    ID: selectionSet.ID,
    Name: selectionSet.Name,
    IsVisible: selectionSet.IsVisible !== false,
    IsForcedVisible: selectionSet.IsForcedVisible === true,
    DisplayOrder: selectionSet.DisplayOrder ?? 0,
    EntitySnapshotIDs: [...(selectionSet.EntitySnapshotIDs ?? [])],
    MediaTileIDs: [...(selectionSet.MediaTileIDs ?? [])],
  };
  if (out.InitialPosition === undefined && selectionSet.IntPosition !== undefined) {
    out.IntPosition = selectionSet.IntPosition;
  }
  if (out.InitialRotation === undefined && selectionSet.IntRotation !== undefined) {
    out.IntRotation = selectionSet.IntRotation;
  }
  if (out.InitialScale === undefined && selectionSet.IntScale !== undefined) {
    out.IntScale = selectionSet.IntScale;
  }
  return out;
}

function stringListOrNull(values: ReadonlyArray<string>): AttributeValue {
  return values.length > 0
    ? { L: values.map((value) => ({ S: value })) }
    : { NULL: true };
}

/** Unity `DynamoHelpers.ConvertVectorToDictionary` — vector as `M{x,y,z}` of `N`. */
function vectorAttribute(v: readonly [number, number, number]): AttributeValue {
  return {
    M: {
      x: { N: String(v[0]) },
      y: { N: String(v[1]) },
      z: { N: String(v[2]) },
    },
  };
}

/** Unity `DynamoHelpers.ConvertVectorToDictionary` — color as `M{r,g,b,a}` of `N`. */
function colorAttribute(color: {
  r: number;
  g: number;
  b: number;
  a: number;
}): AttributeValue {
  return {
    M: {
      r: { N: String(color.r) },
      g: { N: String(color.g) },
      b: { N: String(color.b) },
      a: { N: String(color.a) },
    },
  };
}
